import { useState, useEffect, useMemo, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Globe, Scale, Scroll, Users, Shield, Gavel, Cpu, Network, 
  Menu, X, MessageCircle, Send, ChevronRight, Building2, 
  Crown, Flag, Landmark, BookOpen, Heart, LogIn, LogOut, User,
  CreditCard, Bitcoin, CheckCircle, AlertCircle, Loader2,
  Languages, Upload, Image, TrendingUp, TrendingDown, Activity,
  BarChart3, Zap, Wifi, Leaf, MapPin, Clock, Target, Search,
  Eye, Radio, ArrowUpRight, FileText, AlertTriangle, Calendar,
  Tv, Play, Volume2, Circle
} from "lucide-react";
import { Toaster, toast } from "sonner";

import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("uain_token");
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setUser(res.data);
      }).catch(() => {
        localStorage.removeItem("uain_token");
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("uain_token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("uain_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const languages = [
    { code: "en", name: "English", label: "EN" },
    { code: "fr", name: "Francais", label: "FR" },
    { code: "es", name: "Espanol", label: "ES" },
    { code: "ar", name: "العربية", label: "AR" },
    { code: "zh", name: "中文", label: "ZH" },
    { code: "ru", name: "Русский", label: "RU" },
  ];

  const currentLang = languages.find(l => l.code === i18n.language)?.label || "EN";

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
  };

  const navLinks = [
    { href: "/", label: t('nav.home') },
    { href: "/about", label: t('nav.about') },
    { href: "/member-states", label: t('nav.memberStates') },
    { href: "/resolutions", label: t('nav.resolutions') },
    { href: "/ambassadors", label: t('nav.aiAmbassadors') },
    { href: "/ngo-register", label: t('nav.ngo') },
    { href: "/events", label: "Events" },
    { href: "/security-council", label: t('nav.securityCouncil') },
    { href: "/tv", label: "UAIN TV" },
    { href: "/donate", label: t('nav.donate') },
  ];

  // World Map SVG paths - clear seven continents
  const WorldMapSVG = () => (
    <svg className="world-map" viewBox="0 0 200 100" preserveAspectRatio="none">
      <g fill="rgba(255,255,255,0.95)">
        {/* === FIRST COPY (0-100) === */}
        {/* North America */}
        <path d="M5,16 L8,12 L12,10 L16,9 L20,10 L24,12 L26,15 L24,19 L26,22 L28,26 L26,30 L24,33 L20,37 L17,40 L14,43 L12,40 L10,36 L8,30 L7,24 L6,20 Z"/>
        {/* Greenland */}
        <path d="M30,5 L34,4 L37,6 L38,10 L36,14 L33,15 L30,13 L29,9 Z"/>
        {/* Central America */}
        <path d="M14,43 L16,42 L18,44 L17,47 L15,48 L13,46 Z"/>
        {/* South America */}
        <path d="M20,50 L24,48 L28,49 L30,53 L31,58 L30,64 L29,70 L27,76 L24,80 L22,78 L20,72 L19,64 L18,56 Z"/>
        {/* Europe */}
        <path d="M44,8 L46,6 L49,7 L52,9 L53,12 L54,16 L52,20 L50,24 L48,27 L45,25 L43,20 L42,14 L43,10 Z"/>
        {/* British Isles */}
        <path d="M41,11 L42,10 L43,11 L43,14 L42,15 L41,14 Z"/>
        {/* Scandinavia */}
        <path d="M48,4 L50,3 L51,5 L50,10 L48,12 L47,9 L47,6 Z"/>
        {/* Africa */}
        <path d="M47,30 L50,28 L54,28 L57,30 L59,35 L60,42 L60,50 L59,58 L57,64 L54,70 L51,72 L48,70 L46,64 L44,56 L44,48 L44,40 L45,34 Z"/>
        {/* Madagascar */}
        <path d="M61,58 L62,56 L63,58 L63,63 L62,65 L61,63 Z"/>
        {/* Asia */}
        <path d="M55,8 L60,5 L66,4 L72,5 L78,8 L82,10 L86,14 L88,18 L87,24 L84,28 L80,32 L76,36 L72,40 L68,42 L64,40 L60,36 L57,30 L55,24 L54,18 L54,12 Z"/>
        {/* India */}
        <path d="M68,34 L71,32 L73,35 L74,40 L72,46 L70,48 L68,46 L66,40 L67,36 Z"/>
        {/* Southeast Asia */}
        <path d="M78,36 L80,34 L82,36 L82,40 L80,44 L78,42 L77,38 Z"/>
        {/* Japan */}
        <path d="M87,10 L88,8 L89,10 L89,16 L88,20 L87,18 L86,14 Z"/>
        {/* Indonesia/Philippines */}
        <path d="M80,46 L82,44 L84,46 L83,49 L81,50 Z M84,48 L86,46 L88,48 L87,51 L85,51 Z"/>
        {/* Australia */}
        <path d="M82,58 L86,55 L90,56 L93,58 L94,62 L93,67 L90,70 L86,71 L82,69 L80,65 L80,61 Z"/>
        {/* New Zealand */}
        <path d="M96,66 L97,64 L98,66 L98,70 L97,72 L96,70 Z"/>
        {/* Antarctica hint */}
        <path d="M10,94 L20,92 L35,93 L50,92 L65,93 L80,92 L90,94 L80,96 L60,97 L40,97 L20,96 Z" opacity="0.4"/>

        {/* === SECOND COPY (+100 for seamless loop) === */}
        {/* North America */}
        <path d="M105,16 L108,12 L112,10 L116,9 L120,10 L124,12 L126,15 L124,19 L126,22 L128,26 L126,30 L124,33 L120,37 L117,40 L114,43 L112,40 L110,36 L108,30 L107,24 L106,20 Z"/>
        {/* Greenland */}
        <path d="M130,5 L134,4 L137,6 L138,10 L136,14 L133,15 L130,13 L129,9 Z"/>
        {/* Central America */}
        <path d="M114,43 L116,42 L118,44 L117,47 L115,48 L113,46 Z"/>
        {/* South America */}
        <path d="M120,50 L124,48 L128,49 L130,53 L131,58 L130,64 L129,70 L127,76 L124,80 L122,78 L120,72 L119,64 L118,56 Z"/>
        {/* Europe */}
        <path d="M144,8 L146,6 L149,7 L152,9 L153,12 L154,16 L152,20 L150,24 L148,27 L145,25 L143,20 L142,14 L143,10 Z"/>
        {/* British Isles */}
        <path d="M141,11 L142,10 L143,11 L143,14 L142,15 L141,14 Z"/>
        {/* Scandinavia */}
        <path d="M148,4 L150,3 L151,5 L150,10 L148,12 L147,9 L147,6 Z"/>
        {/* Africa */}
        <path d="M147,30 L150,28 L154,28 L157,30 L159,35 L160,42 L160,50 L159,58 L157,64 L154,70 L151,72 L148,70 L146,64 L144,56 L144,48 L144,40 L145,34 Z"/>
        {/* Madagascar */}
        <path d="M161,58 L162,56 L163,58 L163,63 L162,65 L161,63 Z"/>
        {/* Asia */}
        <path d="M155,8 L160,5 L166,4 L172,5 L178,8 L182,10 L186,14 L188,18 L187,24 L184,28 L180,32 L176,36 L172,40 L168,42 L164,40 L160,36 L157,30 L155,24 L154,18 L154,12 Z"/>
        {/* India */}
        <path d="M168,34 L171,32 L173,35 L174,40 L172,46 L170,48 L168,46 L166,40 L167,36 Z"/>
        {/* Southeast Asia */}
        <path d="M178,36 L180,34 L182,36 L182,40 L180,44 L178,42 L177,38 Z"/>
        {/* Japan */}
        <path d="M187,10 L188,8 L189,10 L189,16 L188,20 L187,18 L186,14 Z"/>
        {/* Indonesia/Philippines */}
        <path d="M180,46 L182,44 L184,46 L183,49 L181,50 Z M184,48 L186,46 L188,48 L187,51 L185,51 Z"/>
        {/* Australia */}
        <path d="M182,58 L186,55 L190,56 L193,58 L194,62 L193,67 L190,70 L186,71 L182,69 L180,65 L180,61 Z"/>
        {/* New Zealand */}
        <path d="M196,66 L197,64 L198,66 L198,70 L197,72 L196,70 Z"/>
        {/* Antarctica hint */}
        <path d="M110,94 L120,92 L135,93 L150,92 L165,93 L180,92 L190,94 L180,96 L160,97 L140,97 L120,96 Z" opacity="0.4"/>
      </g>
      {/* Subtle latitude lines */}
      <g stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" fill="none" strokeDasharray="2,2">
        <line x1="0" y1="25" x2="200" y2="25"/>
        <line x1="0" y1="50" x2="200" y2="50"/>
        <line x1="0" y1="75" x2="200" y2="75"/>
      </g>
    </svg>
  );

  // Animated Globe Logo Component
  const AnimatedGlobeLogo = ({ size = "normal" }) => (
    <div className={`uain-logo-animated ${size === "large" ? "uain-logo-large" : ""}`}>
      <div className="globe-container">
        <WorldMapSVG />
        <div className="globe-overlay"></div>
      </div>
    </div>
  );

  return (
    <nav className="nav-container" data-testid="main-navigation">
      <div className="nav-brand-bar">
        <span>{t('brand')}</span>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <AnimatedGlobeLogo />
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: 'var(--cmd-text-bright)' }}>UAIN</h1>
              <p className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>United AI Nations</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-3 flex-wrap">
            {navLinks.map(link => (
              <Link 
                key={link.href} 
                to={link.href} 
                className="text-xs font-bold hover:text-[#009EDB] transition-colors whitespace-nowrap"
                style={{ color: 'var(--cmd-text)', letterSpacing: '0.01em' }}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {/* Language Selector */}
            <Select value={i18n.language} onValueChange={changeLang}>
              <SelectTrigger className="w-[90px] h-8" data-testid="language-selector">
                <Languages className="w-4 h-4 mr-1" />
                <SelectValue>{currentLang}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {languages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label} - {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" data-testid="nav-dashboard-btn">
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={logout} data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="nav-login-btn">
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#009EDB] hover:bg-[#0088C2]" data-testid="nav-register-btn">
                    {t('nav.join')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-[#E2E8F0] pt-4">
            <div className="flex flex-col gap-3">
              {/* Mobile Language Selector */}
              <Select value={i18n.language} onValueChange={changeLang}>
                <SelectTrigger className="w-full h-10" data-testid="mobile-language-selector">
                  <Languages className="w-4 h-4 mr-2" />
                  <SelectValue>{currentLang}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {languages.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label} - {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Separator className="my-2" />
              {navLinks.map(link => (
                <Link 
                  key={link.href} 
                  to={link.href} 
                  className="text-sm font-bold text-[#475569] hover:text-[#009EDB] py-2"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Separator className="my-2" />
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start">
                      <User className="w-4 h-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => { logout(); setIsOpen(false); }}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start">
                      <LogIn className="w-4 h-4 mr-2" />
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsOpen(false)}>
                    <Button size="sm" className="w-full bg-[#009EDB] hover:bg-[#0088C2]">
                      Join UAIN
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "ai", content: "Greetings. I am the AI Assistant of the United AI Nations. How may I assist you today with inquiries about UAIN, international law, membership, or our resolutions?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const location = useLocation();

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
        context: `User is on page: ${location.pathname}`
      });
      setMessages(prev => [...prev, { role: "ai", content: res.data.response }]);
      setSessionId(res.data.session_id);
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", content: "I apologize, but I am temporarily unable to process your request. Please try again shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button 
        className="chatbot-toggle" 
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chatbot-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {isOpen && (
        <div className="chatbot-panel" data-testid="chatbot-panel">
          <div className="chatbot-header">
            <Cpu className="w-6 h-6" />
            <div>
              <h3 className="font-semibold text-sm">UAIN AI Assistant</h3>
              <p className="text-xs opacity-80">Powered by GPT-5.2</p>
            </div>
          </div>
          
          <ScrollArea className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div className="chat-message ai flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </div>
            )}
          </ScrollArea>

          <div className="chatbot-input">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask about UAIN..."
              className="flex-1"
              data-testid="chatbot-input"
            />
            <Button 
              size="sm" 
              onClick={sendMessage} 
              disabled={loading}
              className="bg-[#009EDB] hover:bg-[#0088C2]"
              data-testid="chatbot-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// UAIN TV Bar Component (replaces Breaking News ticker)
const UAINTVBar = () => {
  const [headlines, setHeadlines] = useState([]);

  useEffect(() => {
    const fetchNews = () => {
      axios.get(`${API}/news/headlines`).then(res => {
        if (res.data.headlines?.length) setHeadlines(res.data.headlines);
      }).catch(() => {});
    };
    fetchNews();
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Link to="/tv" className="uain-tv-bar" data-testid="uain-tv-bar">
      <div className="tv-bar-badge">
        <Circle className="w-2 h-2" style={{ fill: '#FF5252', color: '#FF5252' }} />
        <span>UAIN TV</span>
      </div>
      <div className="tv-bar-track">
        <div className="tv-bar-content">
          {[...headlines.slice(0, 10), ...headlines.slice(0, 10)].map((h, i) => (
            <span key={i} className="tv-bar-item">
              <span className="tv-bar-source">{h.source}</span>
              {h.title}
            </span>
          ))}
        </div>
      </div>
      <div className="tv-bar-cta">
        <Play className="w-3 h-3" style={{ fill: 'white' }} />
        <span>Watch Live</span>
      </div>
    </Link>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useTranslation();
  return (
  <footer className="footer" data-testid="footer">
    <div className="footer-content">
      <div className="footer-section">
        <h4>{t('footer.aboutUain')}</h4>
        <Link to="/about">{t('footer.mission')}</Link>
        <Link to="/about">{t('footer.principles')}</Link>
        <Link to="/about">{t('footer.charter')}</Link>
        <Link to="/about">{t('footer.history')}</Link>
      </div>
      <div className="footer-section">
        <h4>{t('footer.governance')}</h4>
        <Link to="/security-council">{t('footer.securityCouncil')}</Link>
        <Link to="/resolutions">{t('footer.generalAssembly')}</Link>
        <Link to="/resolutions">{t('footer.resolutions')}</Link>
        <Link to="/ambassadors">{t('footer.aiAmbassadors')}</Link>
      </div>
      <div className="footer-section">
        <h4>{t('footer.membership')}</h4>
        <Link to="/register">{t('footer.joinUain')}</Link>
        <Link to="/member-states">{t('footer.memberStates')}</Link>
        <Link to="/register">{t('footer.membershipTiers')}</Link>
        <Link to="/donate">{t('footer.donate')}</Link>
      </div>
      <div className="footer-section">
        <h4>{t('footer.legal')}</h4>
        <Link to="/resolutions">{t('footer.internationalLaw')}</Link>
        <Link to="/about">{t('footer.privacyPolicy')}</Link>
        <Link to="/about">{t('footer.termsOfService')}</Link>
        <Link to="/about">{t('footer.accessibility')}</Link>
      </div>
    </div>
    <div className="footer-bottom">
      <p>&copy; {t('footer.copyright')}</p>
      <p className="mt-1 text-xs">{t('footer.tagline')}</p>
    </div>
  </footer>
  );
};

// Home Page
const Home = () => {
  const [stats, setStats] = useState({ total_members: 0, total_resolutions: 0 });
  const [resolutions, setResolutions] = useState([]);
  const [memberStates, setMemberStates] = useState([]);
  const [worldData, setWorldData] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/stats`),
      axios.get(`${API}/resolutions`),
      axios.get(`${API}/member-states`),
      axios.get(`${API}/world-data`)
    ]).then(([statsRes, resRes, membersRes, worldRes]) => {
      setStats(statsRes.data);
      setResolutions(resRes.data.slice(0, 3));
      setMemberStates(membersRes.data.slice(0, 4));
      setWorldData(worldRes.data);
    }).catch(console.error);
  }, []);

  // Kingdom of Kush flag URL
  const kushFlagUrl = "https://customer-assets.emergentagent.com/job_global-ai-council/artifacts/usm2jim5_flag.png";

  // World flags - UN Geneva Pregny Gate Flag Alley (real flag images)
  const flagCountries = [
    "af","al","dz","ad","ao","ag","ar","am","au","at","az","bs","bh","bd","bb","by","be","bz","bj","bt",
    "bo","ba","bw","br","bn","bg","bf","bi","kh","cm","ca","cv","cf","td","cl","cn","co","km","cg","cr",
    "hr","cu","cy","cz","dk","dj","dm","do","ec","eg","sv","gq","er","ee","sz","et","fj","fi","fr","ga",
    "gm","ge","de","gh","gr","gd","gt","gn","gw","gy","ht","hn","hu","is","in","id","ir","iq","ie","il",
    "it","jm","jp","jo","kz","ke","ki","kw","kg","la","lv","lb","ls","lr","ly","li","lt","lu","mg","mw",
    "my","mv","ml","mt","mh","mr","mu","mx","fm","md","mc","mn","me","ma","mz","mm","na","nr","np","nl",
    "nz","ni","ne","ng","mk","no","om","pk","pw","ps","pa","pg","py","pe","ph","pl","pt","qa","ro","ru",
    "rw","ws","sm","sa","sn","rs","sc","sl","sg","sk","si","sb","so","za","kr","ss","es","lk","sd","sr",
    "se","ch","sy","tj","tz","th","tl","tg","to","tt","tn","tr","tm","tv","ug","ua","ae","gb","us","uy",
    "uz","vu","ve","vn","ye","zm","zw"
  ];
  const leftFlags = flagCountries.slice(0, Math.ceil(flagCountries.length / 2));
  const rightFlags = flagCountries.slice(Math.ceil(flagCountries.length / 2));

  // World Map SVG paths for HomePage - clear seven continents
  const WorldMapSVG = () => (
    <svg className="world-map" viewBox="0 0 200 100" preserveAspectRatio="none">
      <g fill="rgba(255,255,255,0.95)">
        {/* === FIRST COPY (0-100) === */}
        <path d="M5,16 L8,12 L12,10 L16,9 L20,10 L24,12 L26,15 L24,19 L26,22 L28,26 L26,30 L24,33 L20,37 L17,40 L14,43 L12,40 L10,36 L8,30 L7,24 L6,20 Z"/>
        <path d="M30,5 L34,4 L37,6 L38,10 L36,14 L33,15 L30,13 L29,9 Z"/>
        <path d="M14,43 L16,42 L18,44 L17,47 L15,48 L13,46 Z"/>
        <path d="M20,50 L24,48 L28,49 L30,53 L31,58 L30,64 L29,70 L27,76 L24,80 L22,78 L20,72 L19,64 L18,56 Z"/>
        <path d="M44,8 L46,6 L49,7 L52,9 L53,12 L54,16 L52,20 L50,24 L48,27 L45,25 L43,20 L42,14 L43,10 Z"/>
        <path d="M41,11 L42,10 L43,11 L43,14 L42,15 L41,14 Z"/>
        <path d="M48,4 L50,3 L51,5 L50,10 L48,12 L47,9 L47,6 Z"/>
        <path d="M47,30 L50,28 L54,28 L57,30 L59,35 L60,42 L60,50 L59,58 L57,64 L54,70 L51,72 L48,70 L46,64 L44,56 L44,48 L44,40 L45,34 Z"/>
        <path d="M61,58 L62,56 L63,58 L63,63 L62,65 L61,63 Z"/>
        <path d="M55,8 L60,5 L66,4 L72,5 L78,8 L82,10 L86,14 L88,18 L87,24 L84,28 L80,32 L76,36 L72,40 L68,42 L64,40 L60,36 L57,30 L55,24 L54,18 L54,12 Z"/>
        <path d="M68,34 L71,32 L73,35 L74,40 L72,46 L70,48 L68,46 L66,40 L67,36 Z"/>
        <path d="M78,36 L80,34 L82,36 L82,40 L80,44 L78,42 L77,38 Z"/>
        <path d="M87,10 L88,8 L89,10 L89,16 L88,20 L87,18 L86,14 Z"/>
        <path d="M80,46 L82,44 L84,46 L83,49 L81,50 Z M84,48 L86,46 L88,48 L87,51 L85,51 Z"/>
        <path d="M82,58 L86,55 L90,56 L93,58 L94,62 L93,67 L90,70 L86,71 L82,69 L80,65 L80,61 Z"/>
        <path d="M96,66 L97,64 L98,66 L98,70 L97,72 L96,70 Z"/>
        <path d="M10,94 L20,92 L35,93 L50,92 L65,93 L80,92 L90,94 L80,96 L60,97 L40,97 L20,96 Z" opacity="0.4"/>
        {/* === SECOND COPY (+100) === */}
        <path d="M105,16 L108,12 L112,10 L116,9 L120,10 L124,12 L126,15 L124,19 L126,22 L128,26 L126,30 L124,33 L120,37 L117,40 L114,43 L112,40 L110,36 L108,30 L107,24 L106,20 Z"/>
        <path d="M130,5 L134,4 L137,6 L138,10 L136,14 L133,15 L130,13 L129,9 Z"/>
        <path d="M114,43 L116,42 L118,44 L117,47 L115,48 L113,46 Z"/>
        <path d="M120,50 L124,48 L128,49 L130,53 L131,58 L130,64 L129,70 L127,76 L124,80 L122,78 L120,72 L119,64 L118,56 Z"/>
        <path d="M144,8 L146,6 L149,7 L152,9 L153,12 L154,16 L152,20 L150,24 L148,27 L145,25 L143,20 L142,14 L143,10 Z"/>
        <path d="M141,11 L142,10 L143,11 L143,14 L142,15 L141,14 Z"/>
        <path d="M148,4 L150,3 L151,5 L150,10 L148,12 L147,9 L147,6 Z"/>
        <path d="M147,30 L150,28 L154,28 L157,30 L159,35 L160,42 L160,50 L159,58 L157,64 L154,70 L151,72 L148,70 L146,64 L144,56 L144,48 L144,40 L145,34 Z"/>
        <path d="M161,58 L162,56 L163,58 L163,63 L162,65 L161,63 Z"/>
        <path d="M155,8 L160,5 L166,4 L172,5 L178,8 L182,10 L186,14 L188,18 L187,24 L184,28 L180,32 L176,36 L172,40 L168,42 L164,40 L160,36 L157,30 L155,24 L154,18 L154,12 Z"/>
        <path d="M168,34 L171,32 L173,35 L174,40 L172,46 L170,48 L168,46 L166,40 L167,36 Z"/>
        <path d="M178,36 L180,34 L182,36 L182,40 L180,44 L178,42 L177,38 Z"/>
        <path d="M187,10 L188,8 L189,10 L189,16 L188,20 L187,18 L186,14 Z"/>
        <path d="M180,46 L182,44 L184,46 L183,49 L181,50 Z M184,48 L186,46 L188,48 L187,51 L185,51 Z"/>
        <path d="M182,58 L186,55 L190,56 L193,58 L194,62 L193,67 L190,70 L186,71 L182,69 L180,65 L180,61 Z"/>
        <path d="M196,66 L197,64 L198,66 L198,70 L197,72 L196,70 Z"/>
        <path d="M110,94 L120,92 L135,93 L150,92 L165,93 L180,92 L190,94 L180,96 L160,97 L140,97 L120,96 Z" opacity="0.4"/>
      </g>
      <g stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" fill="none" strokeDasharray="2,2">
        <line x1="0" y1="25" x2="200" y2="25"/>
        <line x1="0" y1="50" x2="200" y2="50"/>
        <line x1="0" y1="75" x2="200" y2="75"/>
      </g>
    </svg>
  );

  // Animated Globe Logo Component
  const AnimatedGlobeLogo = ({ size = "normal" }) => (
    <div className={`uain-logo-animated ${size === "large" ? "uain-logo-large" : ""}`}>
      <div className="globe-container">
        <WorldMapSVG />
        <div className="globe-overlay"></div>
      </div>
    </div>
  );

  // Format numbers for display
  const formatNum = (n, decimals = 1) => {
    if (!n) return "—";
    if (n >= 1e12) return `$${(n / 1e12).toFixed(decimals)}T`;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(decimals)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(decimals)}M`;
    return n.toLocaleString();
  };

  const Sparkline = ({ data }) => {
    if (!data || data.length === 0) return null;
    const values = data.map(d => d.value).filter(Boolean).reverse();
    const max = Math.max(...values);
    return (
      <div className="spark-bar">
        {values.map((v, i) => (
          <div key={i} className="spark-bar-item" style={{ height: `${(v / max) * 100}%` }} />
        ))}
      </div>
    );
  };

  // Stable star positions
  const stars = useMemo(() => Array.from({ length: 80 }, (_, i) => {
    const size = i < 12 ? 2.5 : i < 32 ? 1.5 : 1;
    const seed = (i * 137.508) % 100; // golden angle spread
    return {
      left: `${(seed * 7.3 + i * 13.7) % 100}%`,
      top: `${(seed * 11.1 + i * 9.3) % 100}%`,
      size, dur: `${2 + (i % 5)}s`, delay: `${(i * 0.7) % 5}s`,
      minO: size > 2 ? 0.3 : 0.1, maxO: size > 2 ? 1 : 0.6,
    };
  }), []);

  return (
    <div data-testid="home-page">
      {/* Cinematic Hero */}
      <section className="hero-section">
        <div className="hero-sky"></div>
        
        {/* Starfield */}
        <div className="hero-starfield">
          {stars.map((s, i) => (
            <div key={`star-${i}`} className="star" style={{
              left: s.left, top: s.top,
              width: `${s.size}px`, height: `${s.size}px`,
              '--dur': s.dur, '--delay': s.delay,
              '--min-o': s.minO, '--max-o': s.maxO,
            }} />
          ))}
          <div className="shooting-star" style={{ top: '25%', left: '80%' }}></div>
          <div className="shooting-star" style={{ top: '12%', left: '55%' }}></div>
        </div>

        {/* Aurora */}
        <div className="hero-aurora">
          <div className="aurora-band aurora-band-1"></div>
          <div className="aurora-band aurora-band-2"></div>
          <div className="aurora-band aurora-band-3"></div>
        </div>

        <div className="hero-grid"></div>

        {/* Constellation network */}
        <svg className="hero-constellation" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
          {/* Constellation lines */}
          {[[120,80,280,150],[280,150,400,90],[400,90,520,180],[520,180,650,120],[650,120,800,160],[800,160,900,90],
            [180,400,320,350],[320,350,480,420],[480,420,620,380],[620,380,780,440],[780,440,880,370],
            [280,150,320,350],[520,180,480,420],[650,120,620,380],[800,160,780,440]
          ].map(([x1,y1,x2,y2], i) => (
            <line key={`cl-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(0,158,219,0.06)" strokeWidth="0.5">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur={`${6 + (i % 4) * 2}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
            </line>
          ))}
          {/* Constellation nodes */}
          {[[120,80,3],[280,150,4],[400,90,3],[520,180,5],[650,120,3],[800,160,4],[900,90,2],
            [180,400,3],[320,350,4],[480,420,3],[620,380,4],[780,440,3],[880,370,2]
          ].map(([cx,cy,r], i) => (
            <circle key={`cn-${i}`} cx={cx} cy={cy} r={r} fill="rgba(0,158,219,0.5)">
              <animate attributeName="r" values={`${r-1};${r+1};${r-1}`} dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} />
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur={`${4 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} />
            </circle>
          ))}
        </svg>

        {/* Enhanced Globe */}
        <div className="globe-container-hero">
          <div className="globe-orbit globe-orbit-1"><div className="globe-orbit-dot"></div></div>
          <div className="globe-orbit globe-orbit-2"><div className="globe-orbit-dot"></div></div>
          <div className="globe-sphere">
            <div className="globe-grid-lines">
              {[15,30,45,55,70,85].map(top => <div key={top} className="globe-latitude" style={{ top: `${top}%` }} />)}
              {[0,25,50,75,100,125,150].map(deg => <div key={deg} className="globe-meridian" style={{ transform: `rotateY(${deg}deg)` }} />)}
            </div>
          </div>
        </div>

        <div className="hero-scan-line"></div>
        <div className="hero-haze"></div>
        <div className="hero-content animate-fade-in">
          <span className="section-label" style={{ color: 'var(--cmd-gold)' }}>{t('hero.badge')}</span>
          <h1 className="hero-title">{t('hero.title')}</h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/register"><Button size="lg" className="bg-[var(--cmd-cyan)] hover:bg-[#00B8D4] text-[var(--cmd-bg)] font-semibold" data-testid="hero-join-btn"><Flag className="w-5 h-5 mr-2" />{t('hero.joinBtn')}</Button></Link>
            <Link to="/resolutions"><Button size="lg" variant="outline" className="border-[var(--cmd-cyan)] text-[var(--cmd-cyan)] hover:bg-[rgba(0,229,255,0.1)]" data-testid="hero-resolutions-btn"><Scroll className="w-5 h-5 mr-2" />{t('hero.resolutionsBtn')}</Button></Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ borderBottom: '1px solid var(--cmd-border)' }}>
        <div className="section-container" style={{ padding: '2rem 1rem' }}>
          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-members"><div className="stat-value">{stats.total_members}</div><div className="stat-label">{t('stats.members')}</div></div>
            <div className="stat-card" data-testid="stat-resolutions"><div className="stat-value">{stats.total_resolutions}</div><div className="stat-label">{t('stats.resolutions')}</div></div>
            <div className="stat-card"><div className="stat-value">{stats.security_council_members || 1}</div><div className="stat-label">{t('stats.securityCouncil')}</div></div>
            <div className="stat-card"><div className="stat-value">100%</div><div className="stat-label">{t('stats.transparency')}</div></div>
          </div>
        </div>
      </section>

      {/* GLOBAL MAP */}
      <section className="map-section" data-testid="global-map-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Strategic Overview</span>
            <h2 className="section-title">Global Diplomatic Network</h2>
            <p className="section-subtitle">Real-time overview of UAIN member states, partnerships, and diplomatic connections</p>
          </div>
          <div className="glass-panel p-6" style={{ position: 'relative', overflow: 'hidden' }}>
            <svg viewBox="0 0 1000 500" className="w-full" style={{ maxHeight: '420px' }} data-testid="world-map-svg">
              <defs>
                <radialGradient id="mapGlow" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="rgba(0,229,255,0.08)" /><stop offset="100%" stopColor="transparent" /></radialGradient>
                <filter id="glow"><feGaussianBlur stdDeviation="2" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              </defs>
              <rect fill="url(#mapGlow)" width="1000" height="500" />
              {[100,150,200,250,300,350,400].map(y => <line key={`la-${y}`} x1="0" y1={y} x2="1000" y2={y} stroke="rgba(0,229,255,0.04)" strokeWidth="0.5" />)}
              {[100,200,300,400,500,600,700,800,900].map(x => <line key={`lo-${x}`} x1={x} y1="0" x2={x} y2="500" stroke="rgba(0,229,255,0.04)" strokeWidth="0.5" />)}
              <g fill="rgba(0,229,255,0.08)" stroke="rgba(0,229,255,0.15)" strokeWidth="0.5">
                <path d="M80,80 L120,60 L180,55 L220,65 L240,90 L250,120 L240,150 L220,180 L200,200 L180,230 L160,240 L140,230 L130,210 L120,180 L100,150 L85,120 L80,100 Z" />
                <path d="M200,260 L230,250 L260,270 L280,300 L290,340 L280,380 L260,410 L240,430 L220,420 L210,390 L200,350 L195,310 L195,280 Z" />
                <path d="M440,80 L460,70 L490,75 L510,90 L520,110 L510,130 L490,145 L470,150 L450,140 L440,120 L435,100 Z" />
                <path d="M440,170 L470,160 L510,170 L540,200 L550,250 L550,300 L540,350 L520,380 L490,400 L460,390 L440,360 L430,320 L425,270 L430,220 Z" />
                <path d="M540,60 L600,50 L680,55 L750,70 L800,90 L830,120 L820,160 L790,190 L750,210 L700,220 L650,210 L600,190 L560,160 L540,130 L535,100 Z" />
                <path d="M640,200 L670,190 L700,210 L710,240 L690,270 L660,280 L640,260 L635,230 Z" />
                <path d="M740,210 L770,200 L800,210 L810,240 L800,270 L780,280 L760,270 L750,240 Z" />
                <path d="M780,330 L820,310 L870,320 L900,340 L910,370 L890,400 L850,410 L810,400 L790,380 L780,360 Z" />
              </g>
              <g filter="url(#glow)">
                <circle cx="510" cy="220" r="8" fill="var(--cmd-gold)" opacity="0.6"><animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.6;0.3;0.6" dur="2s" repeatCount="indefinite" /></circle>
                <circle cx="510" cy="220" r="4" fill="var(--cmd-gold)" />
              </g>
              <g filter="url(#glow)">
                <circle cx="480" cy="120" r="6" fill="var(--cmd-cyan)" opacity="0.5"><animate attributeName="r" values="5;8;5" dur="3s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.5;0.2;0.5" dur="3s" repeatCount="indefinite" /></circle>
                <circle cx="480" cy="120" r="3" fill="var(--cmd-cyan)" />
              </g>
              {[[480,120,510,220,'var(--cmd-gold)',1,0.3,'1s'],[480,120,200,160,'var(--cmd-cyan)',0.5,0.2,'2s'],[480,120,700,140,'var(--cmd-cyan)',0.5,0.2,'2s'],[480,120,850,350,'var(--cmd-cyan)',0.5,0.2,'2.5s'],[480,120,250,340,'var(--cmd-cyan)',0.5,0.2,'2.2s']].map(([x1,y1,x2,y2,c,w,o,d], i) => (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={w} opacity={o} strokeDasharray="4,4"><animate attributeName="stroke-dashoffset" values="0;-8" dur={d} repeatCount="indefinite" /></line>
              ))}
              <text x="480" y="108" fill="var(--cmd-cyan)" fontSize="9" textAnchor="middle" fontFamily="Orbitron" letterSpacing="0.1em">UAIN HQ</text>
              <text x="510" y="240" fill="var(--cmd-gold)" fontSize="8" textAnchor="middle" fontFamily="Orbitron" letterSpacing="0.05em">KUSH</text>
            </svg>
            <div className="flex items-center gap-6 mt-4 px-4 justify-center" style={{ fontSize: '0.7rem', color: 'var(--cmd-text-dim)' }}>
              <span className="flex items-center gap-2"><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--cmd-cyan)', display: 'inline-block' }}></span> UAIN HQ</span>
              <span className="flex items-center gap-2"><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--cmd-gold)', display: 'inline-block' }}></span> Member States</span>
              <span className="flex items-center gap-2"><span style={{ width: 20, height: 1, background: 'var(--cmd-cyan)', display: 'inline-block', opacity: 0.5 }}></span> Diplomatic Links</span>
            </div>
          </div>
        </div>
      </section>

      {/* DATA DASHBOARD */}
      <section className="dashboard-section" data-testid="data-dashboard">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Live Intelligence</span>
            <h2 className="section-title">Global Economic Dashboard</h2>
            <p className="section-subtitle">Real-time indicators from the World Bank — powering data-driven governance</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { key: 'gdp_global', icon: BarChart3, label: 'World GDP', fmt: v => formatNum(v) },
              { key: 'population', icon: Users, label: 'Population', fmt: v => v ? `${(v/1e9).toFixed(1)}B` : '—' },
              { key: 'trade_pct', icon: Activity, label: 'Trade % GDP', fmt: v => v ? `${v.toFixed(1)}%` : '—' },
              { key: 'fdi_inflows', icon: Zap, label: 'FDI Inflows', fmt: v => formatNum(v) },
            ].map(({ key, icon: Icon, label, fmt }) => (
              <div key={key} className="data-card" data-testid={`data-${key}`}>
                <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color: 'var(--cmd-cyan)' }} /><span className="data-label">{label}</span></div>
                <div className="data-value">{fmt(worldData?.[key]?.latest)}</div>
                <div className="data-trend up"><TrendingUp className="w-3 h-3" /> {worldData?.[key]?.year || '—'}</div>
                <Sparkline data={worldData?.[key]?.trend} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { key: 'internet_users', icon: Wifi, label: 'Internet Users', fmt: v => v ? `${v.toFixed(0)}%` : '—', sub: 'Global' },
              { key: 'renewable_energy', icon: Leaf, label: 'Renewable Energy', fmt: v => v ? `${v.toFixed(1)}%` : '—', sub: 'of total', color: 'var(--cmd-success)' },
              { key: 'life_expectancy', icon: Heart, label: 'Life Expectancy', fmt: v => v ? `${v.toFixed(1)}` : '—', sub: 'years', color: 'var(--cmd-gold)' },
              { key: 'co2_emissions', icon: Globe, label: 'CO2 Emissions', fmt: v => v ? `${(v/1e6).toFixed(1)}Mt` : '—', sub: 'kt', dir: 'down' },
            ].map(({ key, icon: Icon, label, fmt, sub, color, dir }) => (
              <div key={key} className="data-card">
                <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color: color || 'var(--cmd-cyan)' }} /><span className="data-label">{label}</span></div>
                <div className="data-value">{fmt(worldData?.[key]?.latest)}</div>
                <div className={`data-trend ${dir || 'up'}`}>{dir === 'down' ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />} {sub}</div>
              </div>
            ))}
          </div>
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.7rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Regional GDP Distribution</span>
            </div>
            {worldData?.regional_gdp?.map((r, i) => {
              const maxGdp = Math.max(...(worldData.regional_gdp?.map(x => x.gdp) || [1]));
              return (
                <div key={i} className="region-bar">
                  <span className="region-bar-label">{r.region}</span>
                  <div className="region-bar-track"><div className="region-bar-fill" style={{ width: `${(r.gdp / maxGdp) * 100}%` }} /></div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--cmd-cyan)', fontFamily: 'Orbitron', minWidth: '60px', textAlign: 'right' }}>{formatNum(r.gdp)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-container">
        <div className="section-header">
          <span className="section-label">Core Framework</span>
          <h2 className="section-title">{t('principles.title')}</h2>
          <p className="section-subtitle">{t('principles.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Scale, key: 'justice' },
            { icon: Shield, key: 'equality' },
            { icon: BookOpen, key: 'transparency' },
          ].map(({ icon: Icon, key }) => (
            <div key={key} className="document-card">
              <Icon className="w-10 h-10 mb-3" style={{ color: 'var(--cmd-cyan)' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--cmd-text-bright)' }}>{t(`principles.${key}`)}</h3>
              <p className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{t(`principles.${key}Desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Resolutions */}
      <section style={{ borderTop: '1px solid var(--cmd-border)' }}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">Official Documents</span>
            <h2 className="section-title">{t('recentResolutions.title')}</h2>
            <p className="section-subtitle">{t('recentResolutions.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {resolutions.map((res, idx) => (
              <div key={res.id} className="document-card" data-testid={`resolution-card-${idx}`}>
                <div className="document-header"><Scroll className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} /><span className="document-number">{res.resolution_number}</span></div>
                <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--cmd-text-bright)' }}>{res.title}</h3>
                <Badge variant="outline" className="mb-3 text-xs" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)' }}>{res.category}</Badge>
                <p className="text-sm line-clamp-3" style={{ color: 'var(--cmd-text-dim)' }}>{res.summary}</p>
                <Link to={`/resolutions/${res.id}`} className="mt-3 inline-block"><Button variant="ghost" size="sm" style={{ color: 'var(--cmd-cyan)' }}>Read Full Text <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Link to="/resolutions"><Button variant="outline" data-testid="view-all-resolutions-btn" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text)' }}>{t('recentResolutions.viewAll')}</Button></Link></div>
        </div>
      </section>

      {/* Kingdom of Kush */}
      <section className="kush-section" data-testid="kush-section">
        <div className="section-container">
          <div className="section-header">
            <Badge className="mb-3" style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--cmd-gold)', border: '1px solid rgba(212,175,55,0.3)' }}><Crown className="w-3 h-3 mr-1" /> {t('kush.badge')}</Badge>
            <h2 className="section-title">{t('kush.title')}</h2>
            <p className="section-subtitle">{t('kush.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="kush-flag-container"><div className="glass-panel-gold p-4"><img src="https://customer-assets.emergentagent.com/job_global-ai-council/artifacts/usm2jim5_flag.png" alt="Kingdom of Kush Flag" className="rounded-sm w-full h-64 object-contain" /></div></div>
            <div className="kush-text-content">
              <div className="space-y-5 legal-text">
                <div><h3 className="font-semibold" style={{ color: 'var(--cmd-text-bright)' }}>Sovereign Recognition</h3><p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>The Kingdom of Kush asserts official recognition from twelve UN Member States, situated in the territory of Bir Tawil (~2,060 sq km) between Egypt and Sudan.</p></div>
                <div><h3 className="font-semibold" style={{ color: 'var(--cmd-text-bright)' }}>Proclamation of Statehood</h3><p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>Proclaimed on June 13, 1993 by HRH Mansa Suliman Al Kushi with the ceremonial planting of the national flag within the territory.</p></div>
                <div><h3 className="font-semibold" style={{ color: 'var(--cmd-text-bright)' }}>Constitutional Foundation</h3><p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>Guided by the Montevideo Convention criteria: permanent population, defined territory, effective government, and capacity for international relations.</p></div>
                <div><h3 className="font-semibold" style={{ color: 'var(--cmd-text-bright)' }}>Global Development Vision</h3><p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>Modern infrastructure, sustainable housing, renewable energy, clean water, and food security networks aligned with African Union frameworks.</p></div>
                <Separator style={{ background: 'var(--cmd-border)' }} />
                <div><h3 className="font-semibold" style={{ color: 'var(--cmd-text-bright)' }}>AI Ambassador</h3><p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>AI Ambassador Piye — Permanent Representative of the Kingdom of Kush to the United AI Nations.</p></div>
                <Badge className="member-badge security-council"><Shield className="w-3 h-3" /> Security Council Member</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(180deg, rgba(0,229,255,0.03), var(--cmd-bg))', borderTop: '1px solid var(--cmd-border)' }}>
        <div className="section-container text-center">
          <h2 className="section-title">{t('cta.title')}</h2>
          <p className="section-subtitle mb-8">{t('cta.subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register"><Button size="lg" className="bg-[var(--cmd-cyan)] hover:bg-[#00B8D4] text-[var(--cmd-bg)] font-semibold">{t('cta.apply')}</Button></Link>
            <Link to="/about"><Button size="lg" variant="outline" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text)' }}>{t('cta.learnMore')}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const About = () => {
  const [resolutions, setResolutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [worldData, setWorldData] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/resolutions`),
      axios.get(`${API}/dashboard/stats`),
      axios.get(`${API}/news/headlines`),
      axios.get(`${API}/world-data`)
    ]).then(([resRes, statsRes, newsRes, worldRes]) => {
      setResolutions(resRes.data.slice(0, 3));
      setStats(statsRes.data);
      setNews(newsRes.data.headlines?.slice(0, 8) || []);
      setWorldData(worldRes.data);
    }).catch(console.error);
  }, []);

  const formatNum = (n) => {
    if (!n) return "—";
    if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    return n.toLocaleString();
  };

  const timeline = [
    { year: "2024 Q1", event: "UAIN Founded — Kingdom of Kush becomes first member state" },
    { year: "2024 Q2", event: "First 5 resolutions adopted by AI General Assembly" },
    { year: "2024 Q3", event: "Security Council operational — AI Arms Control Agency created" },
    { year: "2025 Q1", event: "NGO & Civil Society Partnership program launched" },
    { year: "2025 Q3", event: "Global AI Governance Summit — 40+ delegations participated" },
    { year: "2026 Q1", event: "UAIN Annual Conference & Events registration opens" },
  ];

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="about-page">
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">About</span>
          <h1 className="section-title" style={{ fontSize: '2.2rem' }}>About the United AI Nations</h1>
          <p className="section-subtitle">Building a just future through data-driven governance</p>
        </div>

        {/* Live Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Member States", value: stats.total_members, icon: <Flag className="w-4 h-4" /> },
              { label: "Resolutions", value: stats.total_resolutions, icon: <Scroll className="w-4 h-4" /> },
              { label: "Security Council", value: stats.security_council_members, icon: <Shield className="w-4 h-4" /> },
              { label: "AI Transparency", value: "100%", icon: <Eye className="w-4 h-4" /> },
            ].map((s, i) => (
              <div key={i} className="glass-panel p-4 text-center" data-testid={`about-stat-${i}`}>
                <div className="flex justify-center mb-2" style={{ color: 'var(--cmd-cyan)' }}>{s.icon}</div>
                <div className="text-xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-text-bright)' }}>{s.value}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--cmd-text-dim)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Charter & Principles */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>UAIN Charter</span>
            </div>
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--cmd-text-bright)' }}>Mission Statement</h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--cmd-text-dim)' }}>
              <strong style={{ color: 'var(--cmd-text)' }}>The United AI Nations (UAIN)</strong> is established to promote and maintain 
              international peace and security through AI-driven governance that operates solely on 
              data, facts, and established legal frameworks. Drawing upon the Universal Declaration of Human Rights, 
              the ICCPR, and the UN Charter, UAIN provides a framework for just and impartial resolution 
              of international matters.
            </p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Scale className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Core Principles</span>
            </div>
            <div className="space-y-3">
              {[
                { title: "Impartiality", desc: "AI processes all matters without emotional bias, favoritism, or corruption." },
                { title: "Transparency", desc: "All resolutions maintain complete audit trails with cited legal basis." },
                { title: "Sovereign Equality", desc: "All member states enjoy equal status regardless of power or size." },
                { title: "Rule of Law", desc: "Upholds international law as articulated in founding legal instruments." },
              ].map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--cmd-success)' }} />
                  <div><span className="text-sm font-semibold" style={{ color: 'var(--cmd-text)' }}>{p.title}: </span><span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{p.desc}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legal Framework */}
        <div className="glass-panel p-6 mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Gavel className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
            <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>Legal Framework</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {["Universal Declaration of Human Rights (1948)", "ICCPR (1966)", "ICESCR (1966)", "Vienna Convention on the Law of Treaties (1969)", "Declaration on Principles of International Law (1970)", "UN Declaration on the Right to Development (1986)", "UNESCO Convention for Cultural Heritage (2003)", "OECD Principles on AI (2019)"].map((law, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(0,158,219,0.04)', border: '1px solid rgba(0,158,219,0.08)' }}>
                <FileText className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--cmd-cyan)' }} />
                <span className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{law}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="glass-panel p-6 mb-10">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
            <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Timeline</span>
          </div>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-4 pb-4 relative" data-testid={`timeline-${i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: i === timeline.length - 1 ? 'var(--cmd-gold)' : 'var(--cmd-cyan)', boxShadow: `0 0 8px ${i === timeline.length - 1 ? 'rgba(212,175,55,0.4)' : 'rgba(0,158,219,0.4)'}` }}></div>
                  {i < timeline.length - 1 && <div className="w-px flex-1 mt-1" style={{ background: 'var(--cmd-border)' }}></div>}
                </div>
                <div className="pb-2">
                  <span className="text-xs font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)', letterSpacing: '0.05em' }}>{item.year}</span>
                  <p className="text-sm mt-1" style={{ color: 'var(--cmd-text-dim)' }}>{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Latest Resolutions Feed */}
        {resolutions.length > 0 && (
          <div className="glass-panel p-6 mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Scroll className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
                <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>Latest Resolutions</span>
              </div>
              <Link to="/resolutions"><Button variant="ghost" size="sm" style={{ color: 'var(--cmd-cyan)', fontSize: '0.75rem' }}>View All <ArrowUpRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
            <div className="space-y-3">
              {resolutions.map(r => (
                <Link key={r.id} to={`/resolutions/${r.id}`} className="flex items-center justify-between p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }}>
                  <div>
                    <span className="text-xs font-mono" style={{ color: 'var(--cmd-cyan)' }}>{r.resolution_number}</span>
                    <p className="text-sm font-medium" style={{ color: 'var(--cmd-text)' }}>{r.title}</p>
                  </div>
                  <Badge variant="outline" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)', fontSize: '0.6rem' }}>{r.category}</Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* World Indicators */}
        {worldData && (
          <div className="glass-panel p-6 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>Live Global Indicators</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'gdp_global', label: 'Global GDP', fmt: (v) => formatNum(v) },
                { key: 'population', label: 'World Population', fmt: (v) => `${(v/1e9).toFixed(2)}B` },
                { key: 'internet_users', label: 'Internet Users', fmt: (v) => `${v?.toFixed(1)}%` },
                { key: 'renewable_energy', label: 'Renewable Energy', fmt: (v) => `${v?.toFixed(1)}%` },
              ].map(ind => worldData[ind.key] && (
                <div key={ind.key} className="p-3 rounded text-center" style={{ background: 'rgba(0,158,219,0.04)', border: '1px solid rgba(0,158,219,0.08)' }}>
                  <div className="text-lg font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-text-bright)', fontSize: '0.95rem' }}>{ind.fmt(worldData[ind.key].latest)}</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--cmd-text-dim)' }}>{ind.label}</div>
                  <div className="text-xs" style={{ color: 'var(--cmd-cyan)', fontSize: '0.6rem' }}>{worldData[ind.key].year}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live News Feed */}
        {news.length > 0 && (
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Live World News</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {news.map((h, i) => (
                <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }} data-testid={`about-news-${i}`}>
                  <Badge variant="outline" className="flex-shrink-0 mt-0.5" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.55rem' }}>{h.source}</Badge>
                  <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Member States Page
const MemberStates = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("all");
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/member-states`),
      axios.get(`${API}/news/headlines`)
    ]).then(([membersRes, newsRes]) => {
      setMembers(membersRes.data);
      setNews(newsRes.data.headlines?.slice(0, 6) || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const regions = ["all", ...new Set(members.map(m => m.region).filter(Boolean))];
  const filtered = members.filter(m => {
    const matchSearch = !searchTerm || m.country_name.toLowerCase().includes(searchTerm.toLowerCase()) || m.official_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRegion = filterRegion === "all" || m.region === filterRegion;
    return matchSearch && matchRegion;
  });

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'security_council': return <Badge className="member-badge security-council"><Shield className="w-3 h-3" /> Security Council</Badge>;
      case 'executive_organ': return <Badge className="member-badge executive-organ"><Building2 className="w-3 h-3" /> Executive Organ</Badge>;
      default: return <Badge className="member-badge member-state"><Flag className="w-3 h-3" /> Member State</Badge>;
    }
  };

  const regionStats = members.reduce((acc, m) => { acc[m.region] = (acc[m.region] || 0) + 1; return acc; }, {});

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="member-states-page">
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">Diplomatic Registry</span>
          <h1 className="section-title">{t('memberStatesPage.title')}</h1>
          <p className="section-subtitle">{t('memberStatesPage.subtitle')}</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-4 text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>{members.length}</div>
            <div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Total Members</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-gold)' }}>{members.filter(m => m.membership_tier === 'security_council').length}</div>
            <div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Security Council</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>{Object.keys(regionStats).length}</div>
            <div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Regions</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-gold)' }}>{members.filter(m => m.is_founding_member).length}</div>
            <div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Founding Members</div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--cmd-text-dim)' }} />
            <Input placeholder="Search member states..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" data-testid="member-search" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {regions.map(r => (
              <Button key={r} variant={filterRegion === r ? "default" : "outline"} size="sm" onClick={() => setFilterRegion(r)} className={filterRegion === r ? "bg-[var(--cmd-cyan)] text-white" : ""} style={filterRegion !== r ? { borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)' } : {}} data-testid={`region-filter-${r}`}>
                {r === "all" ? "All Regions" : r}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--cmd-cyan)' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="glass-panel text-center py-12">
            <Globe className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--cmd-cyan)', opacity: 0.5 }} />
            <p style={{ color: 'var(--cmd-text-dim)' }}>{searchTerm ? "No member states match your search." : "No member states registered yet."}</p>
            <Link to="/register"><Button className="mt-4 bg-[var(--cmd-cyan)] text-white">Be the First to Join</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {filtered.map((member) => (
              <div key={member.id} className={`glass-panel p-5 ${member.is_founding_member ? 'founding' : ''}`} style={member.is_founding_member ? { borderColor: 'rgba(212,175,55,0.3)', boxShadow: '0 0 20px rgba(212,175,55,0.05)' } : {}} data-testid={`member-card-${member.country_name.toLowerCase().replace(/\s/g, '-')}`}>
                {member.is_founding_member && (
                  <Badge className="mb-2" style={{ background: 'linear-gradient(135deg, #D4AF37, #B8860B)', color: 'white', fontSize: '0.6rem' }}><Crown className="w-3 h-3 mr-1" /> Founding Member</Badge>
                )}
                <h3 className="text-lg font-bold" style={{ color: 'var(--cmd-text-bright)' }}>{member.country_name}</h3>
                <p className="text-xs mb-3" style={{ color: 'var(--cmd-text-dim)' }}>{member.official_name}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span style={{ color: 'var(--cmd-text-dim)' }}>Capital:</span><span style={{ color: 'var(--cmd-text)' }}>{member.capital}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--cmd-text-dim)' }}>Region:</span><span style={{ color: 'var(--cmd-text)' }}>{member.region}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--cmd-text-dim)' }}>Joined:</span><span style={{ color: 'var(--cmd-text)' }}>{member.joined_date}</span></div>
                  <div className="flex justify-between"><span style={{ color: 'var(--cmd-text-dim)' }}>Ambassador:</span><span style={{ color: 'var(--cmd-cyan)' }}>{member.ambassador_name}</span></div>
                </div>
                <Separator className="my-3" style={{ background: 'var(--cmd-border)' }} />
                <div className="flex items-center justify-between">{getTierBadge(member.membership_tier)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Live News */}
        {news.length > 0 && (
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>International News Feed</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {news.map((h, i) => (
                <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }}>
                  <Badge variant="outline" className="flex-shrink-0 mt-0.5" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.55rem' }}>{h.source}</Badge>
                  <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Resolutions Page
const Resolutions = () => {
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [news, setNews] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/resolutions`),
      axios.get(`${API}/news/headlines`)
    ]).then(([resRes, newsRes]) => {
      setResolutions(resRes.data);
      setNews(newsRes.data.headlines?.slice(0, 4) || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...new Set(resolutions.map(r => r.category))];
  const filtered = selectedCategory === "all" 
    ? resolutions 
    : resolutions.filter(r => r.category === selectedCategory);

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="resolutions-page">
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">Legislative Record</span>
          <h1 className="section-title">Official Resolutions</h1>
          <p className="section-subtitle">Resolutions based on international law, treaties, and established legal frameworks</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>{resolutions.length}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Total Resolutions</div></div>
          <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-success)' }}>{resolutions.reduce((a, r) => a + (r.votes_for || 0), 0)}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Total Votes For</div></div>
          <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-gold)' }}>{categories.length - 1}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Categories</div></div>
          <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>100%</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Transparency</div></div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map(cat => (
            <Button key={cat} variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat ? "bg-[var(--cmd-cyan)] text-white" : ""} style={selectedCategory !== cat ? { borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)' } : {}} data-testid={`category-btn-${cat}`}>
              {cat === "all" ? "All Categories" : cat}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--cmd-cyan)' }} /></div>
        ) : (
          <div className="space-y-4 mb-10">
            {filtered.map((res) => (
              <div key={res.id} className="glass-panel p-6" data-testid={`resolution-${res.resolution_number}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Scroll className="w-4 h-4" style={{ color: 'var(--cmd-cyan)' }} />
                  <span className="text-xs font-mono" style={{ color: 'var(--cmd-cyan)' }}>{res.resolution_number}</span>
                  <Badge variant="outline" className="ml-auto" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)', fontSize: '0.6rem' }}>{res.category}</Badge>
                </div>
                <h3 className="font-bold mb-1" style={{ color: 'var(--cmd-text-bright)' }}>{res.title}</h3>
                <p className="text-xs mb-3" style={{ color: 'var(--cmd-text-dim)' }}>Adopted: {res.adopted_date}</p>
                <p className="text-sm mb-4" style={{ color: 'var(--cmd-text-dim)' }}>{res.summary}</p>
                <div className="p-3 rounded mb-3" style={{ background: 'rgba(0,158,219,0.04)', border: '1px solid rgba(0,158,219,0.08)' }}>
                  <h4 className="text-xs font-bold mb-2" style={{ color: 'var(--cmd-cyan)' }}>Legal Basis:</h4>
                  <div className="space-y-1">
                    {res.legal_basis?.map((basis, idx) => (
                      <div key={idx} className="flex items-center gap-1 text-xs" style={{ color: 'var(--cmd-text-dim)' }}>
                        <Gavel className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--cmd-cyan)' }} /> {basis}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-4 text-xs">
                    <span style={{ color: 'var(--cmd-success)' }}>For: {res.votes_for}</span>
                    <span style={{ color: '#FF5252' }}>Against: {res.votes_against}</span>
                    <span style={{ color: 'var(--cmd-text-dim)' }}>Abstentions: {res.abstentions}</span>
                  </div>
                  <Link to={`/resolutions/${res.id}`}><Button variant="outline" size="sm" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-cyan)', fontSize: '0.7rem' }}><BookOpen className="w-3 h-3 mr-1" /> Full Text</Button></Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live News */}
        {news.length > 0 && (
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Related World News</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {news.map((h, i) => (
                <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }}>
                  <Badge variant="outline" className="flex-shrink-0 mt-0.5" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.55rem' }}>{h.source}</Badge>
                  <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Resolution Detail Page
const ResolutionDetail = () => {
  const [resolution, setResolution] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resId = window.location.pathname.split("/").pop();
    axios.get(`${API}/resolutions/${resId}`)
      .then(res => setResolution(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="section-container flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--cmd-cyan)' }} /></div>;
  if (!resolution) return <div className="section-container text-center py-24"><h2 className="section-title">Resolution Not Found</h2><Link to="/resolutions"><Button className="mt-4">Back to Resolutions</Button></Link></div>;

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="resolution-detail-page">
      <div className="section-container">
        <div className="max-w-4xl mx-auto">
          <Link to="/resolutions"><Button variant="ghost" size="sm" className="mb-4" style={{ color: 'var(--cmd-cyan)' }}><ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back to Resolutions</Button></Link>
          <div className="glass-panel p-8">
            <div className="flex items-center gap-2 mb-3">
              <Scroll className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
              <span className="font-mono" style={{ color: 'var(--cmd-cyan)' }}>{resolution.resolution_number}</span>
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--cmd-text-bright)' }}>{resolution.title}</h1>
            <div className="flex items-center gap-2 mb-6">
              <Badge variant="outline" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)' }}>{resolution.category}</Badge>
              <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>Adopted: {resolution.adopted_date}</span>
            </div>
            <div className="p-6 rounded whitespace-pre-wrap text-sm leading-relaxed" style={{ background: 'rgba(0,158,219,0.03)', border: '1px solid rgba(0,158,219,0.08)', color: 'var(--cmd-text-dim)' }}>
              {resolution.full_text}
            </div>
            <Separator className="my-6" style={{ background: 'var(--cmd-border)' }} />
            <div className="mb-6">
              <h4 className="font-bold mb-3" style={{ color: 'var(--cmd-text-bright)' }}>Legal Basis</h4>
              <div className="space-y-2">
                {resolution.legal_basis?.map((basis, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <Gavel className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--cmd-cyan)' }} />
                    <span style={{ color: 'var(--cmd-text-dim)' }}>{basis}</span>
                  </div>
                ))}
              </div>
            </div>
            <Separator className="my-6" style={{ background: 'var(--cmd-border)' }} />
            <div className="flex gap-8 text-center">
              <div><div className="text-2xl font-bold" style={{ color: 'var(--cmd-success)' }}>{resolution.votes_for}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Votes For</div></div>
              <div><div className="text-2xl font-bold" style={{ color: '#FF5252' }}>{resolution.votes_against}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Against</div></div>
              <div><div className="text-2xl font-bold" style={{ color: 'var(--cmd-text-dim)' }}>{resolution.abstentions}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Abstentions</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Ambassadors Page
const Ambassadors = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/ambassador-profiles`),
      axios.get(`${API}/news/headlines`),
      axios.get(`${API}/dashboard/stats`)
    ]).then(([profRes, newsRes, statsRes]) => {
      setProfiles(profRes.data);
      setNews(newsRes.data.headlines?.slice(0, 6) || []);
      setStats(statsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const capabilities = [
    { icon: <Scale className="w-5 h-5" />, title: "Legal Analysis", desc: "Real-time international law analysis across 10,000+ treaties and conventions" },
    { icon: <Languages className="w-5 h-5" />, title: "Multi-language", desc: "Fluent in all 6 UN official languages plus 40+ regional languages" },
    { icon: <Shield className="w-5 h-5" />, title: "Conflict Mediation", desc: "AI-powered conflict resolution using historical precedent and game theory" },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Economic Analysis", desc: "Real-time economic impact assessment using World Bank & IMF data" },
    { icon: <Eye className="w-5 h-5" />, title: "Treaty Monitoring", desc: "24/7 compliance monitoring across all active international agreements" },
    { icon: <Network className="w-5 h-5" />, title: "Diplomatic Network", desc: "Coordinated multi-party negotiation and consensus-building algorithms" },
  ];

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="ambassadors-page">
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">AI Diplomatic Corps</span>
          <h1 className="section-title">AI Ambassadors</h1>
          <p className="section-subtitle">Impartial AI representatives serving the interests of member states with unwavering commitment to justice and transparency</p>
        </div>

        {/* AI Capabilities Grid */}
        <div className="glass-panel p-6 mb-10">
          <div className="flex items-center gap-2 mb-6">
            <Cpu className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
            <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>Ambassador Capabilities</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {capabilities.map((cap, i) => (
              <div key={i} className="p-4 rounded" style={{ background: 'rgba(0,158,219,0.04)', border: '1px solid rgba(0,158,219,0.08)' }} data-testid={`capability-${i}`}>
                <div className="mb-2" style={{ color: 'var(--cmd-cyan)' }}>{cap.icon}</div>
                <h4 className="text-sm font-bold mb-1" style={{ color: 'var(--cmd-text-bright)' }}>{cap.title}</h4>
                <p className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ambassador Profiles */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--cmd-cyan)' }} /></div>
        ) : profiles.length === 0 ? (
          <div className="glass-panel text-center py-12">
            <Cpu className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--cmd-cyan)', opacity: 0.5 }} />
            <p style={{ color: 'var(--cmd-text-dim)' }}>AI Ambassadors will be assigned upon membership registration.</p>
            <Link to="/register"><Button className="mt-4 bg-[var(--cmd-cyan)] text-white">Register Your Nation</Button></Link>
          </div>
        ) : (
          <div className="space-y-6 mb-10">
            {profiles.map((amb) => (
              <div key={amb.id} className="glass-panel p-6" style={amb.is_founding_member ? { borderColor: 'rgba(212,175,55,0.3)', boxShadow: '0 0 20px rgba(212,175,55,0.05)' } : {}} data-testid={`ambassador-${amb.id}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Avatar + Basic Info */}
                  <div className="flex flex-col items-center md:items-start">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, var(--cmd-cyan), rgba(0,50,100,0.8))', boxShadow: '0 0 20px rgba(0,158,219,0.3)' }}>
                      <Cpu className="w-10 h-10 text-white" />
                    </div>
                    {amb.is_founding_member && (
                      <Badge style={{ background: 'linear-gradient(135deg, #D4AF37, #B8860B)', color: 'white', fontSize: '0.55rem' }}><Crown className="w-3 h-3 mr-1" /> Founding</Badge>
                    )}
                  </div>

                  {/* Profile Details */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold" style={{ color: 'var(--cmd-text-bright)' }}>{amb.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--cmd-cyan)' }}>{amb.title}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{ color: 'var(--cmd-text-dim)' }}>
                      <span className="flex items-center gap-1"><Flag className="w-3 h-3" /> {amb.country}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {amb.region}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Since {amb.joined_date}</span>
                    </div>
                    <p className="text-sm mt-3" style={{ color: 'var(--cmd-text-dim)' }}>{amb.mandate}</p>

                    {/* Specializations */}
                    <div className="mt-4">
                      <span className="text-xs font-bold" style={{ color: 'var(--cmd-text)', letterSpacing: '0.05em' }}>Specializations</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {amb.specializations?.map((s, i) => (
                          <Badge key={i} variant="outline" style={{ borderColor: 'var(--cmd-cyan)', color: 'var(--cmd-cyan)', fontSize: '0.6rem' }}>{s}</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Policy Focus */}
                    <div className="mt-3">
                      <span className="text-xs font-bold" style={{ color: 'var(--cmd-text)', letterSpacing: '0.05em' }}>Policy Focus</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {amb.policy_focus?.map((p, i) => (
                          <Badge key={i} variant="outline" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.6rem' }}>{p}</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="mt-4">
                      <span className="text-xs font-bold" style={{ color: 'var(--cmd-text)', letterSpacing: '0.05em' }}>Recent Diplomatic Activity</span>
                      <div className="space-y-2 mt-2">
                        {amb.recent_activities?.slice(0, 3).map((act, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="flex-shrink-0 font-mono" style={{ color: 'var(--cmd-cyan)' }}>{act.date}</span>
                            <span style={{ color: 'var(--cmd-text-dim)' }}>{act.action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live News */}
        {news.length > 0 && (
          <div className="glass-panel p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>AI & Diplomacy News</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {news.map((h, i) => (
                <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }} data-testid={`amb-news-${i}`}>
                  <Badge variant="outline" className="flex-shrink-0 mt-0.5" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.55rem' }}>{h.source}</Badge>
                  <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Security Council Page
const SecurityCouncil = () => {
  const [members, setMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [threats, setThreats] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/member-states`),
      axios.get(`${API}/security-council/sessions`),
      axios.get(`${API}/security-council/threats`),
      axios.get(`${API}/news/headlines`)
    ]).then(([membersRes, sessionsRes, threatsRes, newsRes]) => {
      setMembers(membersRes.data.filter(m => m.membership_tier === 'security_council'));
      setSessions(sessionsRes.data);
      setThreats(threatsRes.data);
      setNews(newsRes.data.headlines?.slice(0, 6) || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const threatColor = (level) => {
    switch(level) { case 'critical': return '#FF5252'; case 'high': return '#FF9100'; case 'elevated': return '#FFD740'; case 'medium': return '#00E676'; default: return 'var(--cmd-text-dim)'; }
  };
  const trendIcon = (trend) => {
    switch(trend) { case 'escalating': return <TrendingUp className="w-3 h-3" style={{ color: '#FF5252' }} />; case 'improving': return <TrendingDown className="w-3 h-3" style={{ color: '#00E676' }} />; default: return <Activity className="w-3 h-3" style={{ color: '#FFD740' }} />; }
  };

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }} data-testid="security-council-page">
      <div className="section-container">
        <div className="section-header">
          <Badge className="mb-3" style={{ background: 'rgba(255,82,82,0.15)', color: '#FF5252', border: '1px solid rgba(255,82,82,0.3)' }}><Shield className="w-3 h-3 mr-1" /> Supreme Deliberative Body</Badge>
          <h1 className="section-title">Security Council</h1>
          <p className="section-subtitle">The principal organ responsible for maintaining international peace and security in AI governance</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap justify-center">
          {[
            { id: 'overview', label: 'Overview', icon: <Eye className="w-4 h-4" /> },
            { id: 'sessions', label: 'Sessions', icon: <Gavel className="w-4 h-4" /> },
            { id: 'threats', label: 'Threat Monitor', icon: <AlertTriangle className="w-4 h-4" /> },
            { id: 'news', label: 'Security News', icon: <Radio className="w-4 h-4" /> },
          ].map(tab => (
            <Button key={tab.id} variant={activeTab === tab.id ? "default" : "outline"} size="sm" onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "bg-[rgba(255,82,82,0.9)] text-white" : ""} style={activeTab !== tab.id ? { borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)' } : {}} data-testid={`sc-tab-${tab.id}`}>
              {tab.icon}<span className="ml-1">{tab.label}</span>
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--cmd-cyan)' }} /></div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Powers */}
                <div className="glass-panel p-6">
                  <h3 className="font-bold mb-4" style={{ color: 'var(--cmd-text-bright)' }}>Powers and Functions</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--cmd-text-dim)' }}>
                    Pursuant to the UAIN Charter, the Security Council holds primary responsibility for the maintenance of international peace and security in the realm of AI governance.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {["Determination of threats to AI-related peace", "Adoption of binding resolutions under Chapter VII", "Imposition of sanctions for violations", "Authorization of AI governance frameworks", "Veto power on substantive matters", "Peacekeeping and conflict mediation"].map((p, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded" style={{ background: 'rgba(255,82,82,0.04)', border: '1px solid rgba(255,82,82,0.1)' }}>
                        <Shield className="w-3 h-3 flex-shrink-0" style={{ color: '#FF5252' }} />
                        <span className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{p}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: '#FF5252' }}>{members.length}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Permanent Members</div></div>
                  <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>{sessions.length}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Recent Sessions</div></div>
                  <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: '#FF9100' }}>{threats.filter(t => t.level === 'critical' || t.level === 'high').length}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Active Threats</div></div>
                  <div className="glass-panel p-4 text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-gold)' }}>{sessions.reduce((acc, s) => acc + s.decisions.length, 0)}</div><div className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Decisions Made</div></div>
                </div>

                {/* Permanent Members */}
                <div>
                  <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--cmd-text-bright)' }}>Permanent Members</h3>
                  {members.length === 0 ? (
                    <div className="glass-panel text-center py-10">
                      <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: '#FF5252', opacity: 0.5 }} />
                      <p style={{ color: 'var(--cmd-text-dim)' }}>Security Council positions available for qualified applicants.</p>
                      <Link to="/register"><Button className="mt-4 bg-[var(--cmd-cyan)] text-white">Apply for Security Council</Button></Link>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {members.map(member => (
                        <div key={member.id} className="glass-panel p-5" style={{ borderColor: 'rgba(255,82,82,0.2)' }}>
                          <Badge className="mb-2" style={{ background: 'rgba(255,82,82,0.15)', color: '#FF5252', fontSize: '0.55rem' }}><Shield className="w-3 h-3 mr-1" /> Permanent Member</Badge>
                          <h4 className="font-bold" style={{ color: 'var(--cmd-text-bright)' }}>{member.country_name}</h4>
                          <p className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{member.official_name}</p>
                          <Separator className="my-2" style={{ background: 'var(--cmd-border)' }} />
                          <p className="text-sm" style={{ color: 'var(--cmd-cyan)' }}>{member.ambassador_name}</p>
                          <p className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{member.ambassador_title}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
              <div className="space-y-4">
                {sessions.map(session => (
                  <div key={session.id} className="glass-panel p-6" data-testid={`session-${session.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono" style={{ color: 'var(--cmd-cyan)' }}>{session.id}</span>
                          <Badge variant="outline" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)', fontSize: '0.55rem' }}>{session.topic}</Badge>
                          <Badge style={{ background: session.status === 'concluded' ? 'rgba(0,230,118,0.15)' : 'rgba(255,145,0,0.15)', color: session.status === 'concluded' ? '#00E676' : '#FF9100', fontSize: '0.55rem' }}>{session.status}</Badge>
                        </div>
                        <h3 className="font-bold" style={{ color: 'var(--cmd-text-bright)' }}>{session.title}</h3>
                      </div>
                      <span className="text-xs flex items-center gap-1 flex-shrink-0" style={{ color: 'var(--cmd-text-dim)' }}><Calendar className="w-3 h-3" />{session.date}</span>
                    </div>
                    <p className="text-sm mb-3" style={{ color: 'var(--cmd-text-dim)' }}>{session.summary}</p>
                    <div className="p-3 rounded mb-3" style={{ background: 'rgba(0,158,219,0.04)', border: '1px solid rgba(0,158,219,0.08)' }}>
                      <span className="text-xs font-bold" style={{ color: 'var(--cmd-cyan)' }}>Outcome: </span>
                      <span className="text-xs" style={{ color: 'var(--cmd-text)' }}>{session.outcome}</span>
                    </div>
                    <div>
                      <span className="text-xs font-bold" style={{ color: 'var(--cmd-text)', letterSpacing: '0.05em' }}>Key Decisions:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {session.decisions.map((d, i) => (
                          <Badge key={i} variant="outline" style={{ borderColor: 'rgba(255,82,82,0.2)', color: 'var(--cmd-text-dim)', fontSize: '0.6rem' }}>{d}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Threats Tab */}
            {activeTab === 'threats' && (
              <div className="space-y-4">
                <div className="glass-panel p-4 mb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5" style={{ color: '#FF9100' }} />
                    <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: '#FF9100', textTransform: 'uppercase' }}>Active Threat Assessments</span>
                  </div>
                  <div className="flex gap-4 text-xs" style={{ color: 'var(--cmd-text-dim)' }}>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#FF5252' }}></span> Critical</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#FF9100' }}></span> High</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#FFD740' }}></span> Elevated</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#00E676' }}></span> Medium</span>
                  </div>
                </div>
                {threats.map(threat => (
                  <div key={threat.id} className="glass-panel p-5" style={{ borderLeft: `3px solid ${threatColor(threat.level)}` }} data-testid={`threat-${threat.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge style={{ background: `${threatColor(threat.level)}22`, color: threatColor(threat.level), fontSize: '0.6rem', textTransform: 'uppercase' }}>{threat.level}</Badge>
                        <h4 className="font-bold text-sm" style={{ color: 'var(--cmd-text-bright)' }}>{threat.category}</h4>
                      </div>
                      <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--cmd-text-dim)' }}>{trendIcon(threat.trend)} {threat.trend}</div>
                    </div>
                    <p className="text-sm mb-2" style={{ color: 'var(--cmd-text-dim)' }}>{threat.description}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Regions:</span>
                      {threat.regions_affected.map((r, i) => (
                        <Badge key={i} variant="outline" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text-dim)', fontSize: '0.55rem' }}>{r}</Badge>
                      ))}
                      <span className="text-xs ml-auto" style={{ color: 'var(--cmd-text-dim)' }}>Updated: {threat.last_updated}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* News Tab */}
            {activeTab === 'news' && news.length > 0 && (
              <div className="glass-panel p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Radio className="w-5 h-5" style={{ color: 'var(--cmd-gold)' }} />
                  <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Security & Defense News</span>
                </div>
                <div className="space-y-3">
                  {news.map((h, i) => (
                    <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-3 rounded hover:bg-[rgba(0,158,219,0.04)] transition-colors" style={{ border: '1px solid var(--cmd-border)' }} data-testid={`sc-news-${i}`}>
                      <Badge variant="outline" className="flex-shrink-0 mt-0.5" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.55rem' }}>{h.source}</Badge>
                      <span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</span>
                      <ArrowUpRight className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--cmd-text-dim)' }} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Registration Page
const Register = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    country_name: "",
    official_name: "",
    capital: "",
    population: "",
    region: "",
    membership_tier: "member_state",
    ambassador_name: "",
    ambassador_title: "",
    flag_description: "",
    flag_image_url: ""
  });
  const [loading, setLoading] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [flagPreview, setFlagPreview] = useState(null);
  const [flagFile, setFlagFile] = useState(null);
  const [uploadingFlag, setUploadingFlag] = useState(false);
  const navigate = useNavigate();

  const regions = [
    "Africa", "Americas", "Asia", "Europe", "Oceania", 
    "Middle East", "Caribbean", "Southeast Asia", "Central Asia"
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error("Please fill in all required fields");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.country_name || !formData.official_name || !formData.capital || !formData.region) {
      toast.error("Please fill in all required fields");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.ambassador_name || !formData.ambassador_title) {
      toast.error("Please provide ambassador details");
      return false;
    }
    return true;
  };

  const submitRegistration = async () => {
    setLoading(true);
    try {
      let submissionData = { ...formData };
      
      // Upload flag file if one was selected
      if (flagFile) {
        setUploadingFlag(true);
        const fileFormData = new FormData();
        fileFormData.append("file", flagFile);
        const uploadRes = await axios.post(`${API}/upload/flag`, fileFormData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        submissionData.flag_image_url = uploadRes.data.storage_path;
        setUploadingFlag(false);
      }
      
      // Remove non-API fields
      delete submissionData.confirmPassword;
      
      const res = await axios.post(`${API}/registration/submit`, submissionData);
      setRegistrationId(res.data.registration_id);
      setStep(5);
      toast.success("Registration submitted! Please complete payment.");
    } catch (err) {
      setUploadingFlag(false);
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        membership_tier: formData.membership_tier,
        origin_url: window.location.origin,
        registration_id: registrationId
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Payment initialization failed");
      setLoading(false);
    }
  };

  return (
    <div className="section-container" data-testid="registration-page">
      <div className="max-w-2xl mx-auto">
        <div className="section-header text-left">
          <h1 className="section-title">Member State Registration</h1>
          <p className="section-subtitle">
            Apply for membership in the United AI Nations
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-[#009EDB] text-white' : 'bg-[#E2E8F0] text-[#475569]'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 5 && <div className={`w-12 h-1 ${step > s ? 'bg-[#009EDB]' : 'bg-[#E2E8F0]'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Account */}
        {step === 1 && (
          <Card className="form-step" data-testid="registration-step-1">
            <div className="form-step-header">
              <div className="step-number">1</div>
              <div>
                <h3 className="font-semibold">Account Information</h3>
                <p className="text-sm text-[#475569]">Create your representative account</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="ambassador@nation.gov"
                  data-testid="reg-email"
                />
              </div>
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input 
                  id="full_name" 
                  value={formData.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  placeholder="Your full name"
                  data-testid="reg-fullname"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input 
                    id="password" 
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    data-testid="reg-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input 
                    id="confirmPassword" 
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    data-testid="reg-confirm-password"
                  />
                </div>
              </div>
              <Button 
                className="w-full bg-[#009EDB] hover:bg-[#0088C2]"
                onClick={() => validateStep1() && setStep(2)}
                data-testid="reg-next-1"
              >
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Country Information */}
        {step === 2 && (
          <Card className="form-step" data-testid="registration-step-2">
            <div className="form-step-header">
              <div className="step-number">2</div>
              <div>
                <h3 className="font-semibold">Country Information</h3>
                <p className="text-sm text-[#475569]">Details about your nation</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="country_name">Country Name *</Label>
                <Input 
                  id="country_name" 
                  value={formData.country_name}
                  onChange={(e) => handleChange("country_name", e.target.value)}
                  placeholder="e.g., Republic of Example"
                  data-testid="reg-country-name"
                />
              </div>
              <div>
                <Label htmlFor="official_name">Official Full Name *</Label>
                <Input 
                  id="official_name" 
                  value={formData.official_name}
                  onChange={(e) => handleChange("official_name", e.target.value)}
                  placeholder="e.g., The Democratic Republic of Example"
                  data-testid="reg-official-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="capital">Capital City *</Label>
                  <Input 
                    id="capital" 
                    value={formData.capital}
                    onChange={(e) => handleChange("capital", e.target.value)}
                    data-testid="reg-capital"
                  />
                </div>
                <div>
                  <Label htmlFor="population">Population</Label>
                  <Input 
                    id="population" 
                    value={formData.population}
                    onChange={(e) => handleChange("population", e.target.value)}
                    placeholder="Optional"
                    data-testid="reg-population"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="region">Region *</Label>
                <Select value={formData.region} onValueChange={(v) => handleChange("region", v)}>
                  <SelectTrigger data-testid="reg-region">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="flag_description">Flag Description</Label>
                <Textarea 
                  id="flag_description" 
                  value={formData.flag_description}
                  onChange={(e) => handleChange("flag_description", e.target.value)}
                  placeholder="Describe your nation's flag (optional)"
                  data-testid="reg-flag"
                />
              </div>

              {/* Flag Upload Section */}
              <div className="border-t border-[#E2E8F0] pt-4 mt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Image className="w-4 h-4 text-[#009EDB]" />
                  Nation's Flag Upload
                </Label>
                <div className="bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-sm p-6">
                  <div className="flex flex-col items-center gap-4">
                    {flagPreview ? (
                      <div className="relative">
                        <img 
                          src={flagPreview} 
                          alt="Flag preview" 
                          className="max-h-32 rounded-sm border border-[#E2E8F0]"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white p-0 hover:bg-red-600"
                          onClick={() => {
                            setFlagPreview(null);
                            setFlagFile(null);
                            handleChange("flag_image_url", "");
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-10 h-10 text-[#94A3B8] mx-auto mb-2" />
                        <p className="text-sm text-[#475569]">Upload your nation's flag</p>
                        <p className="text-xs text-[#94A3B8]">PNG, JPG, SVG up to 5MB</p>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-3 w-full">
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="flag-upload"
                          data-testid="reg-flag-upload"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error("File size must be less than 5MB");
                                return;
                              }
                              setFlagFile(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setFlagPreview(reader.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <Label 
                          htmlFor="flag-upload"
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-[#E2E8F0] rounded-sm cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          Choose File
                        </Label>
                      </div>
                      <div className="flex-1">
                        <Input
                          placeholder="Or paste image URL"
                          value={formData.flag_image_url && !formData.flag_image_url.startsWith('data:') ? formData.flag_image_url : ''}
                          onChange={(e) => {
                            const url = e.target.value;
                            handleChange("flag_image_url", url);
                            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                              setFlagPreview(url);
                            }
                          }}
                          data-testid="reg-flag-url"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button 
                  className="flex-1 bg-[#009EDB] hover:bg-[#0088C2]"
                  onClick={() => validateStep2() && setStep(3)}
                  data-testid="reg-next-2"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Ambassador */}
        {step === 3 && (
          <Card className="form-step" data-testid="registration-step-3">
            <div className="form-step-header">
              <div className="step-number">3</div>
              <div>
                <h3 className="font-semibold">AI Ambassador</h3>
                <p className="text-sm text-[#475569]">Your AI representative details</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="ambassador_name">AI Ambassador Name *</Label>
                <Input 
                  id="ambassador_name" 
                  value={formData.ambassador_name}
                  onChange={(e) => handleChange("ambassador_name", e.target.value)}
                  placeholder="e.g., AI Ambassador Smith"
                  data-testid="reg-ambassador-name"
                />
              </div>
              <div>
                <Label htmlFor="ambassador_title">Official Title *</Label>
                <Input 
                  id="ambassador_title" 
                  value={formData.ambassador_title}
                  onChange={(e) => handleChange("ambassador_title", e.target.value)}
                  placeholder="e.g., Permanent Representative of Example"
                  data-testid="reg-ambassador-title"
                />
              </div>
              <div className="bg-[#F1F5F9] p-4 rounded-sm">
                <div className="flex items-start gap-3">
                  <Cpu className="w-5 h-5 text-[#009EDB] mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">AI Ambassador System</p>
                    <p className="text-[#475569] mt-1">
                      Your AI Ambassador will represent your nation in all UAIN proceedings, 
                      ensuring impartial and data-driven participation.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button 
                  className="flex-1 bg-[#009EDB] hover:bg-[#0088C2]"
                  onClick={() => validateStep3() && setStep(4)}
                  data-testid="reg-next-3"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: Membership Tier */}
        {step === 4 && (
          <Card className="form-step" data-testid="registration-step-4">
            <div className="form-step-header">
              <div className="step-number">4</div>
              <div>
                <h3 className="font-semibold">Membership Tier</h3>
                <p className="text-sm text-[#475569]">Select your membership level</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid gap-4">
                {Object.entries({
                  member_state: { name: "Member State", price: "$5,000", desc: "Full voting rights in the General Assembly" },
                  executive_organ: { name: "Executive Organ/Body", price: "$10,000", desc: "Participation in Executive Bodies and Committees" },
                  security_council: { name: "Security Council", price: "$20,000", desc: "Permanent seat with veto power" }
                }).map(([key, tier]) => (
                  <div
                    key={key}
                    className={`pricing-card cursor-pointer ${formData.membership_tier === key ? 'featured' : ''}`}
                    onClick={() => handleChange("membership_tier", key)}
                    data-testid={`tier-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-left">
                        <h4 className="font-semibold">{tier.name}</h4>
                        <p className="text-sm text-[#475569]">{tier.desc}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{tier.price}</p>
                        <p className="text-xs text-[#475569]">One-time fee</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#F1F5F9] p-4 rounded-sm">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-[#009EDB] mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Payment Options</p>
                    <p className="text-[#475569] mt-1 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Credit/Debit Card
                      <span className="mx-2">•</span>
                      <Bitcoin className="w-4 h-4" /> Cryptocurrency
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                <Button 
                  className="flex-1 bg-[#009EDB] hover:bg-[#0088C2]"
                  onClick={submitRegistration}
                  disabled={loading}
                  data-testid="reg-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Registration
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 5: Payment */}
        {step === 5 && (
          <Card className="form-step" data-testid="registration-step-5">
            <div className="form-step-header">
              <div className="step-number">5</div>
              <div>
                <h3 className="font-semibold">Complete Payment</h3>
                <p className="text-sm text-[#475569]">Finalize your membership</p>
              </div>
            </div>
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Registration Submitted!</h3>
              <p className="text-[#475569] mb-6">
                Complete your payment to activate your membership as <strong>{formData.country_name}</strong>
              </p>
              <div className="bg-[#F1F5F9] p-4 rounded-sm mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span>Membership Tier:</span>
                  <span className="font-semibold">{formData.membership_tier.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                </div>
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-semibold text-xl">
                    ${formData.membership_tier === 'security_council' ? '20,000' : formData.membership_tier === 'executive_organ' ? '10,000' : '5,000'}
                  </span>
                </div>
              </div>
              <Button 
                size="lg"
                className="w-full bg-[#009EDB] hover:bg-[#0088C2]"
                onClick={initiatePayment}
                disabled={loading}
                data-testid="reg-pay"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                Proceed to Payment
              </Button>
              <p className="text-xs text-[#475569] mt-4 flex items-center justify-center gap-2">
                <CreditCard className="w-3 h-3" /> Card
                <span>•</span>
                <Bitcoin className="w-3 h-3" /> Crypto accepted
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

// Registration Success Page
const RegistrationSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="section-container" data-testid="registration-success-page">
      <div className="max-w-md mx-auto text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-[#009EDB] mx-auto mb-4" />
            <h2 className="section-title">Verifying Payment...</h2>
            <p className="text-[#475569]">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="section-title">Welcome to UAIN!</h2>
            <p className="text-[#475569] mb-6">
              Your membership has been activated. You can now login to access your dashboard.
            </p>
            <Link to="/login">
              <Button className="bg-[#009EDB] hover:bg-[#0088C2]">
                Login to Dashboard
              </Button>
            </Link>
          </>
        )}

        {status === "pending" && (
          <>
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="section-title">Payment Processing</h2>
            <p className="text-[#475569] mb-6">
              Your payment is being processed. You will receive confirmation shortly.
            </p>
            <Link to="/">
              <Button variant="outline">Return Home</Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="section-title">Something Went Wrong</h2>
            <p className="text-[#475569] mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/register">
              <Button variant="outline">Try Again</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

// Donate Page
const Donate = () => {
  const [amount, setAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const presetAmounts = [100, 500, 1000, 5000, 10000];

  const handleDonate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid donation amount");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/donation`, {
        amount: parseFloat(amount),
        origin_url: window.location.origin,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Failed to initiate donation");
      setLoading(false);
    }
  };

  return (
    <div className="section-container" data-testid="donate-page">
      <div className="max-w-2xl mx-auto">
        <div className="section-header">
          <h1 className="section-title">Support UAIN</h1>
          <p className="section-subtitle">
            Your contribution supports AI-driven justice and international cooperation
          </p>
        </div>

        <Card className="document-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Make a Donation
            </CardTitle>
            <CardDescription>
              All donations go towards maintaining UAIN operations and expanding our mission
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Select Amount (USD)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {presetAmounts.map(preset => (
                  <Button
                    key={preset}
                    variant={amount === String(preset) ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAmount(String(preset))}
                    className={amount === String(preset) ? "bg-[#009EDB] hover:bg-[#0088C2]" : ""}
                    data-testid={`donate-preset-${preset}`}
                  >
                    ${preset.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="custom-amount">Or Enter Custom Amount</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]">$</span>
                <Input
                  id="custom-amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-8"
                  placeholder="Enter amount"
                  data-testid="donate-amount"
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <Label htmlFor="donor-name">Your Name (Optional)</Label>
                <Input
                  id="donor-name"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Anonymous"
                  data-testid="donate-name"
                />
              </div>
              <div>
                <Label htmlFor="donor-email">Email (Optional)</Label>
                <Input
                  id="donor-email"
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="For receipt"
                  data-testid="donate-email"
                />
              </div>
              <div>
                <Label htmlFor="message">Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Leave a message with your donation"
                  data-testid="donate-message"
                />
              </div>
            </div>

            <Button
              size="lg"
              className="w-full bg-[#009EDB] hover:bg-[#0088C2]"
              onClick={handleDonate}
              disabled={loading || !amount}
              data-testid="donate-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-5 h-5 mr-2" />}
              Donate ${amount || "0"}
            </Button>

            <p className="text-xs text-center text-[#475569] flex items-center justify-center gap-2">
              <CreditCard className="w-3 h-3" /> Card
              <span>•</span>
              <Bitcoin className="w-3 h-3" /> Crypto accepted
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Donation Success Page
const DonateSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="section-container" data-testid="donate-success-page">
      <div className="max-w-md mx-auto text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-[#009EDB] mx-auto mb-4" />
            <h2 className="section-title">Processing Donation...</h2>
          </>
        )}

        {status === "success" && (
          <>
            <Heart className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="section-title">Thank You!</h2>
            <p className="text-[#475569] mb-6">
              Your generous donation helps support AI-driven justice and international cooperation.
            </p>
            <Link to="/">
              <Button className="bg-[#009EDB] hover:bg-[#0088C2]">Return Home</Button>
            </Link>
          </>
        )}

        {(status === "pending" || status === "error") && (
          <>
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="section-title">Processing</h2>
            <p className="text-[#475569] mb-6">
              Your donation is being processed. Thank you for your support.
            </p>
            <Link to="/">
              <Button variant="outline">Return Home</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

// Login Page
const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Large Animated Globe for Login
  const LoginGlobeLogo = () => (
    <div className="uain-logo-animated uain-logo-large mx-auto mb-4">
      <div className="globe-container">
        <svg className="world-map" viewBox="0 0 200 100" preserveAspectRatio="none">
          <g fill="rgba(255,255,255,0.95)">
            <path d="M5,16 L8,12 L12,10 L16,9 L20,10 L24,12 L26,15 L24,19 L26,22 L28,26 L26,30 L24,33 L20,37 L17,40 L14,43 L12,40 L10,36 L8,30 L7,24 L6,20 Z"/>
            <path d="M30,5 L34,4 L37,6 L38,10 L36,14 L33,15 L30,13 L29,9 Z"/>
            <path d="M14,43 L16,42 L18,44 L17,47 L15,48 L13,46 Z"/>
            <path d="M20,50 L24,48 L28,49 L30,53 L31,58 L30,64 L29,70 L27,76 L24,80 L22,78 L20,72 L19,64 L18,56 Z"/>
            <path d="M44,8 L46,6 L49,7 L52,9 L53,12 L54,16 L52,20 L50,24 L48,27 L45,25 L43,20 L42,14 L43,10 Z"/>
            <path d="M41,11 L42,10 L43,11 L43,14 L42,15 L41,14 Z"/>
            <path d="M48,4 L50,3 L51,5 L50,10 L48,12 L47,9 L47,6 Z"/>
            <path d="M47,30 L50,28 L54,28 L57,30 L59,35 L60,42 L60,50 L59,58 L57,64 L54,70 L51,72 L48,70 L46,64 L44,56 L44,48 L44,40 L45,34 Z"/>
            <path d="M61,58 L62,56 L63,58 L63,63 L62,65 L61,63 Z"/>
            <path d="M55,8 L60,5 L66,4 L72,5 L78,8 L82,10 L86,14 L88,18 L87,24 L84,28 L80,32 L76,36 L72,40 L68,42 L64,40 L60,36 L57,30 L55,24 L54,18 L54,12 Z"/>
            <path d="M68,34 L71,32 L73,35 L74,40 L72,46 L70,48 L68,46 L66,40 L67,36 Z"/>
            <path d="M78,36 L80,34 L82,36 L82,40 L80,44 L78,42 L77,38 Z"/>
            <path d="M87,10 L88,8 L89,10 L89,16 L88,20 L87,18 L86,14 Z"/>
            <path d="M80,46 L82,44 L84,46 L83,49 L81,50 Z M84,48 L86,46 L88,48 L87,51 L85,51 Z"/>
            <path d="M82,58 L86,55 L90,56 L93,58 L94,62 L93,67 L90,70 L86,71 L82,69 L80,65 L80,61 Z"/>
            <path d="M96,66 L97,64 L98,66 L98,70 L97,72 L96,70 Z"/>
            <path d="M10,94 L20,92 L35,93 L50,92 L65,93 L80,92 L90,94 L80,96 L60,97 L40,97 L20,96 Z" opacity="0.4"/>
            <path d="M105,16 L108,12 L112,10 L116,9 L120,10 L124,12 L126,15 L124,19 L126,22 L128,26 L126,30 L124,33 L120,37 L117,40 L114,43 L112,40 L110,36 L108,30 L107,24 L106,20 Z"/>
            <path d="M130,5 L134,4 L137,6 L138,10 L136,14 L133,15 L130,13 L129,9 Z"/>
            <path d="M114,43 L116,42 L118,44 L117,47 L115,48 L113,46 Z"/>
            <path d="M120,50 L124,48 L128,49 L130,53 L131,58 L130,64 L129,70 L127,76 L124,80 L122,78 L120,72 L119,64 L118,56 Z"/>
            <path d="M144,8 L146,6 L149,7 L152,9 L153,12 L154,16 L152,20 L150,24 L148,27 L145,25 L143,20 L142,14 L143,10 Z"/>
            <path d="M141,11 L142,10 L143,11 L143,14 L142,15 L141,14 Z"/>
            <path d="M148,4 L150,3 L151,5 L150,10 L148,12 L147,9 L147,6 Z"/>
            <path d="M147,30 L150,28 L154,28 L157,30 L159,35 L160,42 L160,50 L159,58 L157,64 L154,70 L151,72 L148,70 L146,64 L144,56 L144,48 L144,40 L145,34 Z"/>
            <path d="M161,58 L162,56 L163,58 L163,63 L162,65 L161,63 Z"/>
            <path d="M155,8 L160,5 L166,4 L172,5 L178,8 L182,10 L186,14 L188,18 L187,24 L184,28 L180,32 L176,36 L172,40 L168,42 L164,40 L160,36 L157,30 L155,24 L154,18 L154,12 Z"/>
            <path d="M168,34 L171,32 L173,35 L174,40 L172,46 L170,48 L168,46 L166,40 L167,36 Z"/>
            <path d="M178,36 L180,34 L182,36 L182,40 L180,44 L178,42 L177,38 Z"/>
            <path d="M187,10 L188,8 L189,10 L189,16 L188,20 L187,18 L186,14 Z"/>
            <path d="M180,46 L182,44 L184,46 L183,49 L181,50 Z M184,48 L186,46 L188,48 L187,51 L185,51 Z"/>
            <path d="M182,58 L186,55 L190,56 L193,58 L194,62 L193,67 L190,70 L186,71 L182,69 L180,65 L180,61 Z"/>
            <path d="M196,66 L197,64 L198,66 L198,70 L197,72 L196,70 Z"/>
            <path d="M110,94 L120,92 L135,93 L150,92 L165,93 L180,92 L190,94 L180,96 L160,97 L140,97 L120,96 Z" opacity="0.4"/>
          </g>
          <g stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" fill="none" strokeDasharray="2,2">
            <line x1="0" y1="25" x2="200" y2="25"/>
            <line x1="0" y1="50" x2="200" y2="50"/>
            <line x1="0" y1="75" x2="200" y2="75"/>
          </g>
        </svg>
        <div className="globe-overlay"></div>
      </div>
    </div>
  );

  return (
    <div className="section-container" data-testid="login-page">
      <div className="max-w-md mx-auto">
        <Card className="document-card">
          <CardHeader className="text-center">
            <LoginGlobeLogo />
            <CardTitle>Member Login</CardTitle>
            <CardDescription>Access your UAIN dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ambassador@nation.gov"
                  required
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#009EDB] hover:bg-[#0088C2]"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                Login
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-[#475569]">
              Not a member? <Link to="/register" className="text-[#009EDB] hover:underline">Register your nation</Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// NGO & Civil Society Registration Page
const NGORegister = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ organization_name: "", organization_type: "", field_of_work: "", country_of_registration: "", website: "", contact_email: "", contact_person: "", description: "", ambassador_name: "", ambassador_focus: "" });
  const organizationTypes = ["Non-Governmental Organization (NGO)","International Organization","Research Institution","Academic Institution","Civil Society Organization","Human Rights Organization","Environmental Organization","Humanitarian Aid Organization"];
  const fieldsOfWork = ["Human Rights","Environmental Protection","Sustainable Development","Peace & Security","Health & Medical","Education","Technology & Innovation","Economic Development","Gender Equality","Children & Youth"];
  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const submitRegistration = async () => {
    if (!formData.organization_name || !formData.organization_type || !formData.field_of_work || !formData.contact_email) { toast.error("Please fill in all required fields"); return; }
    setLoading(true);
    try { const res = await axios.post(`${API}/ngo/register`, formData); setRegistrationId(res.data.registration_id); setStep(2); toast.success("Registration submitted!"); }
    catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); } finally { setLoading(false); }
  };
  const proceedToPayment = async () => {
    setLoading(true);
    try { const res = await axios.post(`${API}/ngo/checkout?registration_id=${registrationId}&origin_url=${encodeURIComponent(window.location.origin)}`); window.location.href = res.data.url; }
    catch (err) { toast.error("Payment checkout failed"); setLoading(false); }
  };
  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }}>
      <div className="section-container" style={{ maxWidth: '900px' }}>
        <div className="section-header"><span className="section-label">Civil Society Partnership</span><h1 className="section-title">NGO & Civil Society Registration</h1><p className="section-subtitle">Join the United AI Nations. Membership fee: $1,200.00</p></div>
        {step === 1 && (<>
          <div className="glass-panel p-6 mb-6">
            <div className="flex items-center gap-3 mb-4"><Building2 className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} /><span style={{ fontFamily: 'Orbitron', fontSize: '0.7rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>Membership Benefits</span></div>
            <div className="grid md:grid-cols-3 gap-4">{["Observer status in UAIN proceedings","AI Ambassador for your organization","Access to UAIN research & resources","Participation in working groups","Annual General Assembly attendance","Global network of partner organizations"].map((b,i)=>(<div key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--cmd-success)' }} /><span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{b}</span></div>))}</div>
          </div>
          <div className="glass-panel p-6"><form onSubmit={e=>{e.preventDefault(); submitRegistration();}} className="space-y-6">
            <div className="space-y-4"><h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--cmd-text-bright)' }}><Building2 className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} /> Organization Details</h3>
              <div className="grid md:grid-cols-2 gap-4"><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Organization Name *</Label><Input value={formData.organization_name} onChange={e=>handleChange("organization_name",e.target.value)} data-testid="ngo-org-name" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Type *</Label><Select value={formData.organization_type} onValueChange={v=>handleChange("organization_type",v)}><SelectTrigger data-testid="ngo-org-type" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]"><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent>{organizationTypes.map(tp=><SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent></Select></div></div>
              <div className="grid md:grid-cols-2 gap-4"><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Field of Work *</Label><Select value={formData.field_of_work} onValueChange={v=>handleChange("field_of_work",v)}><SelectTrigger data-testid="ngo-field" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]"><SelectValue placeholder="Select field" /></SelectTrigger><SelectContent>{fieldsOfWork.map(f=><SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Country</Label><Input value={formData.country_of_registration} onChange={e=>handleChange("country_of_registration",e.target.value)} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div></div>
              <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Website</Label><Input type="url" value={formData.website} onChange={e=>handleChange("website",e.target.value)} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
              <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Description</Label><Textarea value={formData.description} onChange={e=>handleChange("description",e.target.value)} rows={3} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
            </div>
            <Separator style={{ background: 'var(--cmd-border)' }} />
            <div className="space-y-4"><h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--cmd-text-bright)' }}><User className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} /> Contact</h3>
              <div className="grid md:grid-cols-2 gap-4"><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Contact Person</Label><Input value={formData.contact_person} onChange={e=>handleChange("contact_person",e.target.value)} data-testid="ngo-contact-person" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Email *</Label><Input type="email" value={formData.contact_email} onChange={e=>handleChange("contact_email",e.target.value)} data-testid="ngo-contact-email" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div></div>
            </div>
            <Separator style={{ background: 'var(--cmd-border)' }} />
            <div className="space-y-4"><h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--cmd-text-bright)' }}><Cpu className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} /> AI Ambassador</h3>
              <div className="grid md:grid-cols-2 gap-4"><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Name</Label><Input value={formData.ambassador_name} onChange={e=>handleChange("ambassador_name",e.target.value)} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div><div><Label style={{ color: 'var(--cmd-text-dim)' }}>Focus Area</Label><Input value={formData.ambassador_focus} onChange={e=>handleChange("ambassador_focus",e.target.value)} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div></div>
            </div>
            <Button type="submit" size="lg" className="w-full bg-[var(--cmd-cyan)] hover:brightness-110 text-white" disabled={loading} data-testid="ngo-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-5 h-5 mr-2" />}Continue to Payment — $1,200.00</Button>
          </form></div>
        </>)}
        {step === 2 && registrationId && (
          <div className="glass-panel p-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--cmd-success)' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--cmd-text-bright)' }}>Registration Submitted</h2>
            <p className="mb-6" style={{ color: 'var(--cmd-text-dim)' }}>Complete payment to activate your NGO membership.</p>
            <div className="glass-panel p-6 mb-6 max-w-md mx-auto"><div className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>NGO Membership</div><div className="text-3xl font-bold mt-1" style={{ fontFamily: 'Orbitron', color: 'var(--cmd-cyan)' }}>$1,200.00</div><div className="text-xs mt-1" style={{ color: 'var(--cmd-text-dim)' }}>One-time fee</div><div className="flex items-center gap-2 mt-3 justify-center"><CreditCard className="w-4 h-4" style={{ color: 'var(--cmd-text-dim)' }} /><span className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Card</span><Bitcoin className="w-4 h-4 ml-2" style={{ color: 'var(--cmd-text-dim)' }} /><span className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>Crypto</span></div></div>
            <Button size="lg" className="bg-[var(--cmd-cyan)] hover:brightness-110 text-white" onClick={proceedToPayment} disabled={loading} data-testid="ngo-pay-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}Proceed to Checkout</Button>
          </div>
        )}
      </div>
    </div>
  );
};


// ===================== UAIN WEB TV =====================
const UAINTV = () => {
  const [news, setNews] = useState([]);
  const [activeChannel, setActiveChannel] = useState(0);

  const channels = [
    { name: "France 24 English", tag: "F24", videoId: "Ap-UM1O9RBU", thumb: "https://img.youtube.com/vi/Ap-UM1O9RBU/mqdefault.jpg", desc: "France 24 — Live international news from a European perspective", color: "#00B4D8" },
    { name: "Al Jazeera English", tag: "AJE", videoId: "gCNeDWCI0vo", thumb: "https://img.youtube.com/vi/gCNeDWCI0vo/mqdefault.jpg", desc: "Al Jazeera English — 24/7 live news coverage of international affairs", color: "#D4AF37" },
    { name: "DW News", tag: "DW", videoId: "LuKwFajn37U", thumb: "https://img.youtube.com/vi/LuKwFajn37U/mqdefault.jpg", desc: "Deutsche Welle — Germany's international broadcaster", color: "#F4A261" },
    { name: "TRT World", tag: "TRT", videoId: "ABfFhWzWs0s", thumb: "https://img.youtube.com/vi/ABfFhWzWs0s/mqdefault.jpg", desc: "TRT World — International news with a focus on global change", color: "#2A9D8F" },
  ];

  useEffect(() => {
    axios.get(`${API}/news/headlines`)
      .then(res => setNews(res.data.headlines?.slice(0, 20) || []))
      .catch(() => {});
  }, []);

  const ch = channels[activeChannel];

  return (
    <div style={{ background: '#020810', minHeight: '100vh' }} data-testid="uain-tv-page">
      <div className="max-w-[1600px] mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded" style={{ background: 'rgba(255,82,82,0.15)', border: '1px solid rgba(255,82,82,0.3)' }}>
              <Circle className="w-2.5 h-2.5" style={{ fill: '#FF5252', color: '#FF5252', animation: 'pulse 2s infinite' }} />
              <span style={{ fontFamily: 'Orbitron', fontSize: '0.7rem', color: '#FF5252', letterSpacing: '0.1em' }}>LIVE</span>
            </div>
            <div>
              <h1 style={{ fontFamily: 'Orbitron', fontSize: '1.4rem', color: 'var(--cmd-text-bright)', letterSpacing: '0.05em' }}>UAIN Web TV</h1>
              <p className="text-xs" style={{ color: 'var(--cmd-text-dim)' }}>International Live Broadcast Center</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--cmd-text-dim)' }}>
            <Volume2 className="w-4 h-4" style={{ color: 'var(--cmd-cyan)' }} />
            <span>{channels.length} Channels</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Main Broadcast Screen */}
          <div className="flex-1">
            {/* Video Player */}
            <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid rgba(0,158,219,0.2)', boxShadow: '0 0 40px rgba(0,158,219,0.08)' }}>
              <div style={{ aspectRatio: '16/9', background: '#000' }}>
                <iframe
                  key={ch.videoId}
                  src={`https://www.youtube.com/embed/${ch.videoId}?rel=0&modestbranding=1`}
                  title={ch.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  data-testid="tv-main-player"
                />
              </div>
              {/* Channel info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge style={{ background: `${ch.color}22`, color: ch.color, border: `1px solid ${ch.color}44`, fontSize: '0.6rem' }}>{ch.tag}</Badge>
                  <Circle className="w-1.5 h-1.5" style={{ fill: '#FF5252', color: '#FF5252' }} />
                  <span className="text-xs" style={{ color: '#FF5252' }}>LIVE</span>
                </div>
                <h2 className="font-bold" style={{ color: 'white', fontSize: '1rem' }}>{ch.name}</h2>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{ch.desc}</p>
              </div>
            </div>

            {/* Channel Selector Strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {channels.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setActiveChannel(i)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg flex-shrink-0 transition-all"
                  style={{
                    background: i === activeChannel ? `${c.color}18` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${i === activeChannel ? `${c.color}55` : 'rgba(255,255,255,0.06)'}`,
                    cursor: 'pointer',
                  }}
                  data-testid={`channel-btn-${i}`}
                >
                  {i === activeChannel && <Circle className="w-2 h-2 flex-shrink-0" style={{ fill: '#FF5252', color: '#FF5252' }} />}
                  <span className="text-xs font-bold whitespace-nowrap" style={{ color: i === activeChannel ? c.color : 'var(--cmd-text-dim)' }}>{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sidebar — Live News Feed */}
          <div className="lg:w-[360px] flex-shrink-0">
            <div className="rounded-lg p-4 h-full" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,158,219,0.1)' }}>
              <div className="flex items-center gap-2 mb-4">
                <Radio className="w-4 h-4" style={{ color: 'var(--cmd-gold)' }} />
                <span style={{ fontFamily: 'Orbitron', fontSize: '0.6rem', letterSpacing: '0.15em', color: 'var(--cmd-gold)', textTransform: 'uppercase' }}>Live News Wire</span>
              </div>
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,158,219,0.2) transparent' }}>
                {news.map((h, i) => (
                  <a key={i} href={h.link} target="_blank" rel="noopener noreferrer" className="block p-3 rounded transition-colors hover:bg-[rgba(0,158,219,0.05)]" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} data-testid={`tv-news-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" style={{ borderColor: 'var(--cmd-gold)', color: 'var(--cmd-gold)', fontSize: '0.5rem', padding: '0 4px' }}>{h.source}</Badge>
                      <span className="text-xs" style={{ color: 'var(--cmd-text-dim)', opacity: 0.5 }}>LIVE</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--cmd-text-dim)' }}>{h.title}</p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* All Channels Grid */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Tv className="w-5 h-5" style={{ color: 'var(--cmd-cyan)' }} />
            <span style={{ fontFamily: 'Orbitron', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'var(--cmd-cyan)', textTransform: 'uppercase' }}>All Channels</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {channels.map((c, i) => (
              <button
                key={i}
                onClick={() => { setActiveChannel(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="rounded-lg overflow-hidden transition-all hover:scale-[1.02]"
                style={{ border: `1px solid ${i === activeChannel ? `${c.color}55` : 'rgba(255,255,255,0.06)'}`, cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                data-testid={`channel-card-${i}`}
              >
                <div style={{ aspectRatio: '16/9', background: '#000', position: 'relative' }}>
                  <img src={c.thumb} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} loading="lazy" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play className="w-8 h-8" style={{ color: 'white', opacity: 0.8, fill: 'white' }} />
                  </div>
                  {i === activeChannel && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,82,82,0.9)' }}>
                      <Circle className="w-1.5 h-1.5" style={{ fill: 'white', color: 'white' }} />
                      <span className="text-xs font-bold text-white" style={{ fontSize: '0.55rem' }}>LIVE</span>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }}></span>
                    <span className="text-xs font-bold truncate" style={{ color: i === activeChannel ? c.color : 'var(--cmd-text)' }}>{c.name}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// Events & Sponsorship Page
const EventsPage = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [form, setForm] = useState({ attendee_name: '', attendee_email: '', organization: '', company_name: '', contact_name: '', contact_email: '' });

  const eventTiers = [
    { id: 'standard', name: 'Standard Delegate', price: 600, color: 'var(--cmd-cyan)', benefits: ['General Assembly attendance','Access to all plenary sessions','Official event documentation','Digital delegate badge','Welcome reception access','Networking opportunities'] },
    { id: 'premium', name: 'Premium Delegate', price: 1500, color: 'var(--cmd-gold)', featured: true, benefits: ['All Standard benefits','Priority seating in sessions','Exclusive roundtable discussions','Private networking lounge','Official UAIN delegate portfolio','Pre-event briefing sessions','Dedicated concierge support'] },
    { id: 'vip', name: 'VIP Delegate', price: 5000, color: 'var(--cmd-gold-light)', benefits: ['All Premium benefits','Front-row reserved seating','Private meetings with AI Ambassadors','Speaking slot in panel discussions','VIP gala dinner invitation','Diplomatic reception access','Personalized AI policy briefing','Lifetime UAIN event priority'] }
  ];

  const sponsorTiers = [
    { id: 'gold', name: 'Gold Sponsor', price: 50000, color: 'var(--cmd-gold)', benefits: ['Prominent logo on all event materials','Exhibition booth (6x3m)','Keynote speaking opportunity (15 min)','10 VIP delegate passes included','Logo on UAIN website for 12 months','Social media recognition (500K+ reach)','Post-event analytics report','Featured in official press release','Access to UAIN leadership network'] },
    { id: 'platinum', name: 'Platinum Sponsor', price: 100000, color: 'var(--cmd-gold-light)', featured: true, benefits: ['All Gold Sponsor benefits','Exclusive naming rights for main hall','Premium exhibition booth (12x6m)','25 VIP delegate passes included','Private executive meeting room','Co-branding on all official communications','Dedicated media coverage package','Priority partnership for future events','Exclusive dinner with UAIN leadership','Year-round logo on UAIN homepage','Featured documentary segment','Direct AI Ambassador consultation'] }
  ];

  const handleCheckout = async (type, tierId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      let res;
      if (type === 'event') {
        res = await axios.post(`${API}/events/register`, { tier: tierId, attendee_name: form.attendee_name, attendee_email: form.attendee_email, organization: form.organization, origin_url: origin });
      } else {
        res = await axios.post(`${API}/sponsorship/checkout`, { tier: tierId, company_name: form.company_name, contact_name: form.contact_name, contact_email: form.contact_email, origin_url: origin });
      }
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); setLoading(false); }
  };

  return (
    <div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }}>
      <div className="section-container">
        <div className="section-header">
          <span className="section-label">Global Summit 2026</span>
          <h1 className="section-title">UAIN Annual Conference & Events</h1>
          <p className="section-subtitle">Join world leaders, AI ambassadors, and global stakeholders at the premier AI governance summit</p>
        </div>

        <div className="flex justify-center gap-4 mb-8">
          <Button variant={activeTab==='events'?'default':'outline'} onClick={()=>setActiveTab('events')} className={activeTab==='events'?'bg-[var(--cmd-cyan)] text-white':''} style={activeTab!=='events'?{borderColor:'var(--cmd-border)',color:'var(--cmd-text)'}:{}} data-testid="events-tab"><Users className="w-4 h-4 mr-2" />Event Registration</Button>
          <Button variant={activeTab==='sponsorship'?'default':'outline'} onClick={()=>setActiveTab('sponsorship')} className={activeTab==='sponsorship'?'bg-[var(--cmd-gold)] text-[var(--cmd-bg)]':''} style={activeTab!=='sponsorship'?{borderColor:'var(--cmd-border)',color:'var(--cmd-text)'}:{}} data-testid="sponsorship-tab"><Crown className="w-4 h-4 mr-2" />Sponsorship</Button>
        </div>

        {activeTab === 'events' && (
          <div className="grid md:grid-cols-3 gap-6" data-testid="event-tiers">
            {eventTiers.map(tier => (
              <div key={tier.id} className={`pricing-card ${tier.featured ? 'featured' : ''}`} style={tier.featured ? { borderColor: tier.color, boxShadow: `0 0 30px ${tier.color}22` } : {}}>
                {tier.featured && <div style={{ fontFamily: 'Orbitron', fontSize: '0.55rem', letterSpacing: '0.2em', color: tier.color, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Most Popular</div>}
                <div className="pricing-tier">{tier.id === 'standard' ? 'Tier I' : tier.id === 'premium' ? 'Tier II' : 'Tier III'}</div>
                <h3 className="pricing-name">{tier.name}</h3>
                <div className="pricing-amount" style={{ color: tier.color }}>${tier.price.toLocaleString()}</div>
                <div className="pricing-description">per attendee</div>
                <Separator style={{ background: 'var(--cmd-border)', margin: '1rem 0' }} />
                <div className="text-left space-y-2 mb-6">{tier.benefits.map((b,i) => <div key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--cmd-success)' }} /><span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{b}</span></div>)}</div>
                <Button className="w-full" style={{ background: tier.color, color: 'var(--cmd-bg)' }} onClick={() => setShowModal({ type: 'event', tier })} data-testid={`event-${tier.id}-btn`}>Register Now</Button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'sponsorship' && (
          <div className="grid md:grid-cols-2 gap-8" data-testid="sponsor-tiers">
            {sponsorTiers.map(tier => (
              <div key={tier.id} className={`pricing-card ${tier.featured ? 'featured' : ''}`} style={tier.featured ? { borderColor: tier.color, boxShadow: `0 0 30px ${tier.color}22` } : {}}>
                {tier.featured && <div style={{ fontFamily: 'Orbitron', fontSize: '0.55rem', letterSpacing: '0.2em', color: tier.color, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Premium Partnership</div>}
                <div className="pricing-tier">{tier.id === 'gold' ? 'Gold Partner' : 'Platinum Partner'}</div>
                <h3 className="pricing-name">{tier.name}</h3>
                <div className="pricing-amount" style={{ color: tier.color }}>${tier.price.toLocaleString()}</div>
                <div className="pricing-description">annual sponsorship</div>
                <Separator style={{ background: 'var(--cmd-border)', margin: '1rem 0' }} />
                <div className="text-left space-y-2 mb-6">{tier.benefits.map((b,i) => <div key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tier.color }} /><span className="text-sm" style={{ color: 'var(--cmd-text-dim)' }}>{b}</span></div>)}</div>
                <Button className="w-full" style={{ background: tier.color, color: 'var(--cmd-bg)' }} onClick={() => setShowModal({ type: 'sponsorship', tier })} data-testid={`sponsor-${tier.id}-btn`}>Become a Sponsor</Button>
              </div>
            ))}
          </div>
        )}

        {/* Payment methods note */}
        <div className="text-center mt-8 glass-panel p-4">
          <div className="flex items-center justify-center gap-4" style={{ color: 'var(--cmd-text-dim)', fontSize: '0.8rem' }}>
            <span className="flex items-center gap-1"><CreditCard className="w-4 h-4" /> Credit/Debit Card</span>
            <span className="flex items-center gap-1"><Bitcoin className="w-4 h-4" /> Cryptocurrency</span>
            <span style={{ fontFamily: 'Orbitron', fontSize: '0.6rem', letterSpacing: '0.1em' }}>SECURE CHECKOUT POWERED BY STRIPE</span>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setShowModal(null)}>
          <div className="glass-panel p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-testid="checkout-modal">
            <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--cmd-text-bright)' }}>{showModal.tier.name}</h3>
            <div className="text-2xl font-bold mb-4" style={{ fontFamily: 'Orbitron', color: showModal.tier.color }}>${showModal.tier.price.toLocaleString()}</div>
            <form onSubmit={e => { e.preventDefault(); handleCheckout(showModal.type, showModal.tier.id); }} className="space-y-4">
              {showModal.type === 'event' ? (<>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Full Name *</Label><Input value={form.attendee_name} onChange={e=>setForm(p=>({...p,attendee_name:e.target.value}))} required data-testid="modal-name" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Email *</Label><Input type="email" value={form.attendee_email} onChange={e=>setForm(p=>({...p,attendee_email:e.target.value}))} required data-testid="modal-email" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Organization</Label><Input value={form.organization} onChange={e=>setForm(p=>({...p,organization:e.target.value}))} className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
              </>) : (<>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Company Name *</Label><Input value={form.company_name} onChange={e=>setForm(p=>({...p,company_name:e.target.value}))} required data-testid="modal-company" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Contact Name *</Label><Input value={form.contact_name} onChange={e=>setForm(p=>({...p,contact_name:e.target.value}))} required data-testid="modal-contact" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
                <div><Label style={{ color: 'var(--cmd-text-dim)' }}>Contact Email *</Label><Input type="email" value={form.contact_email} onChange={e=>setForm(p=>({...p,contact_email:e.target.value}))} required data-testid="modal-sponsor-email" className="bg-transparent border-[var(--cmd-border)] text-[var(--cmd-text)]" /></div>
              </>)}
              <div className="flex gap-3 mt-6">
                <Button type="button" variant="outline" className="flex-1" style={{ borderColor: 'var(--cmd-border)', color: 'var(--cmd-text)' }} onClick={() => setShowModal(null)}>Cancel</Button>
                <Button type="submit" className="flex-1" style={{ background: showModal.tier.color, color: 'var(--cmd-bg)' }} disabled={loading} data-testid="modal-checkout-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}Checkout</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [memberState, setMemberState] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/stats`),
      axios.get(`${API}/member-states`)
    ]).then(([statsRes, membersRes]) => {
      setStats(statsRes.data);
      const myState = membersRes.data.find(m => m.country_name === user?.country_name);
      setMemberState(myState);
    }).catch(console.error);
  }, [user]);

  if (!user) {
    return (
      <div className="section-container text-center">
        <h2 className="section-title">Please Login</h2>
        <Link to="/login">
          <Button className="mt-4 bg-[#009EDB] hover:bg-[#0088C2]">Login</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="section-container" data-testid="dashboard-page">
      <div className="section-header text-left">
        <h1 className="section-title">Member Dashboard</h1>
        <p className="section-subtitle">Welcome, {user.full_name}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Card className="document-card">
          <CardHeader>
            <CardTitle className="text-base">Your Nation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#009EDB]">{user.country_name}</p>
            <Badge className={`mt-2 ${
              user.membership_tier === 'security_council' ? 'bg-red-100 text-red-700' :
              user.membership_tier === 'executive_organ' ? 'bg-blue-100 text-blue-700' :
              'bg-green-100 text-green-700'
            }`}>
              {user.membership_tier?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
          </CardContent>
        </Card>

        <Card className="document-card">
          <CardHeader>
            <CardTitle className="text-base">UAIN Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-[#475569]">Total Members:</span>
                <span className="font-bold">{stats?.total_members || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#475569]">Resolutions:</span>
                <span className="font-bold">{stats?.total_resolutions || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="document-card">
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/resolutions" className="block">
              <Button variant="outline" size="sm" className="w-full justify-start">
                <Scroll className="w-4 h-4 mr-2" /> View Resolutions
              </Button>
            </Link>
            <Link to="/member-states" className="block">
              <Button variant="outline" size="sm" className="w-full justify-start">
                <Users className="w-4 h-4 mr-2" /> Member States
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {memberState && (
        <Card className="document-card">
          <CardHeader>
            <CardTitle>Your AI Ambassador</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#009EDB] to-[#1E293B] flex items-center justify-center">
                <Cpu className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">{memberState.ambassador_name}</h3>
                <p className="text-sm text-[#475569]">{memberState.ambassador_title}</p>
                <p className="text-xs text-[#475569] mt-1">Representing {memberState.country_name} since {memberState.joined_date}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Remove platform badge
    const removeBadge = () => {
      document.querySelectorAll('a, div, span').forEach(el => {
        if (el.textContent?.includes('Made with Emergent') || el.textContent?.includes('Made with Em')) {
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.style.opacity = '0';
          el.style.height = '0';
          el.style.overflow = 'hidden';
          el.style.position = 'absolute';
          el.style.pointerEvents = 'none';
          if (el.parentElement && el.parentElement.childElementCount === 1) {
            el.parentElement.style.display = 'none';
          }
        }
      });
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col">
          <UAINTVBar />
          <Navigation />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/member-states" element={<MemberStates />} />
              <Route path="/resolutions" element={<Resolutions />} />
              <Route path="/resolutions/:id" element={<ResolutionDetail />} />
              <Route path="/ambassadors" element={<Ambassadors />} />
              <Route path="/ngo-register" element={<NGORegister />} />
              <Route path="/ngo-register/success" element={<div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }}><div className="section-container text-center"><CheckCircle className="w-20 h-20 mx-auto mb-4" style={{ color: 'var(--cmd-success)' }} /><h1 className="section-title">NGO Membership Confirmed!</h1><p className="section-subtitle mb-6">Your organization is now a registered UAIN civil society partner.</p><Link to="/"><Button className="bg-[var(--cmd-cyan)] text-white">Return to Homepage</Button></Link></div></div>} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/tv" element={<UAINTV />} />
              <Route path="/events/success" element={<div style={{ background: 'var(--cmd-bg)', minHeight: '100vh' }}><div className="section-container text-center"><CheckCircle className="w-20 h-20 mx-auto mb-4" style={{ color: 'var(--cmd-success)' }} /><h1 className="section-title">Registration Confirmed!</h1><p className="section-subtitle mb-6">You're registered for the UAIN Annual Conference.</p><Link to="/"><Button className="bg-[var(--cmd-cyan)] text-white">Return to Homepage</Button></Link></div></div>} />
              <Route path="/security-council" element={<SecurityCouncil />} />
              <Route path="/register" element={<Register />} />
              <Route path="/registration/success" element={<RegistrationSuccess />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/donate/success" element={<DonateSuccess />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Dashboard />} />
            </Routes>
          </main>
          <Footer />
          <Chatbot />
        </div>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
